import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button, Card, Typography, Space, message, Spin, Progress, Input, Tag, Avatar, Divider, Modal } from 'antd';
import { AudioOutlined, VideoCameraOutlined, StopOutlined, PlayCircleOutlined, CheckCircleOutlined, SoundOutlined, RightOutlined, ArrowRightOutlined, UserOutlined, RobotOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getData, uploadFile } from '../../../actions/apiCall';
import { putData } from '../../../actions/developer';
import { decryptData, encryptData } from '../../../actions/crypto';
import RecordRTC from 'recordrtc';
import ConsentRecording from './ConsentRecording';
import IntroductionRecording from './IntroductionRecording';
import PhotoCapture from './PhotoCapture';
import CountdownTimer from '../ExamRoom/CountdownTimer';
import { examConfig } from '../../../config/examConfig';
import { startTestMonitoring, stopTestMonitoring } from '../../../utils/testMonitoring';
import './AIInterview.scss';

// Type declarations for Web Speech API
declare global {
    interface Window {
        SpeechRecognition: any;
        webkitSpeechRecognition: any;
    }
}

const { Title, Paragraph } = Typography;

// Constants
const ANSWER_TIMER_DURATION = 90; // 1.5 minutes in seconds
const NO_SPEECH_TIMEOUT = 20000; // 20 seconds
const BLOB_PROCESSING_DELAY = 200; // milliseconds
const MIN_RECORDING_TIME = 5; // seconds - minimum 5 seconds for consent
const MIN_CONSENT_TRANSCRIPT_LENGTH = 50; // minimum 50 characters for consent transcript
const MIN_INTRODUCTION_RECORDING_TIME = 10; // seconds - minimum 10 seconds for introduction
const MIN_INTRODUCTION_TRANSCRIPT_WORDS = 50; // minimum 50 words for introduction transcript
const RECORDING_CONFIG = {
    type: 'audio' as const,
    mimeType: 'audio/webm',
    audioBitsPerSecond: 128000,
};
const SPEECH_CONFIG = {
    rate: 0.9,
    pitch: 1,
    volume: 1,
    lang: 'en-US',
};

// Helper functions outside component
const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Text similarity function using word-based comparison
// Helper function to count words in a text
const countWords = (text: string): number => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

const calculateTextSimilarity = (text1: string, text2: string): number => {
    // Normalize texts: lowercase, remove extra spaces, remove punctuation
    const normalize = (text: string): string[] => {
        return text
            .toLowerCase()
            .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim()
            .split(' ')
            .filter(word => word.length > 0);
    };

    const words1 = normalize(text1);
    const words2 = normalize(text2);

    if (words1.length === 0 && words2.length === 0) return 1;
    if (words1.length === 0 || words2.length === 0) return 0;

    // Calculate word overlap
    const set1 = new Set(words1);
    const set2 = new Set(words2);

    const intersection = new Set(Array.from(set1).filter(word => set2.has(word)));
    const union = new Set([...Array.from(set1), ...Array.from(set2)]);

    // Jaccard similarity (intersection over union)
    const jaccardSimilarity = intersection.size / union.size;

    // Also check for substring matches (for partial readings)
    const text1Normalized = words1.join(' ');
    const text2Normalized = words2.join(' ');

    // Check if one text contains most words from the other
    let wordMatchRatio = 0;
    if (words1.length > 0 && words2.length > 0) {
        const matchedWords = words1.filter(word => words2.includes(word)).length;
        wordMatchRatio = matchedWords / Math.max(words1.length, words2.length);
    }

    // Combine Jaccard similarity and word match ratio
    // Weight word match ratio more heavily as it's better for partial matches
    const similarity = (jaccardSimilarity * 0.4) + (wordMatchRatio * 0.6);

    return Math.min(1, similarity);
};

const resetAnswerState = (
    setAnswerSubmitted: (val: boolean) => void,
    setRecordedBlob: (val: Blob | null) => void,
    setRecordingTime: (val: number) => void,
    setTranscription: (val: string) => void,
    setAnswerScore: (val: number | null) => void,
    setPromptSpoken: (val: boolean) => void,
    setIsAnswerTimerActive: (val: boolean) => void,
    setAnswerTimer: (val: number) => void,
    transcriptionRef: React.MutableRefObject<string>,
    finalTranscriptsRef: React.MutableRefObject<string[]>,
    interimTranscriptRef: React.MutableRefObject<string>
) => {
    setAnswerSubmitted(false);
    setRecordedBlob(null);
    setRecordingTime(0);
    setTranscription('');
    setAnswerScore(null);
    setPromptSpoken(false);
    setIsAnswerTimerActive(false);
    setAnswerTimer(ANSWER_TIMER_DURATION);
    transcriptionRef.current = '';
    finalTranscriptsRef.current = [];
    interimTranscriptRef.current = '';
};

interface Question {
    question: string;
    questionNumber: number;
    totalQuestions: number;
    hasMoreQuestions?: boolean;
    questionId?: string;
}

interface AIInterviewProps {
    testDetails?: any;
    onSubmit?: () => void;
}

const AIInterview: React.FC<AIInterviewProps> = ({ testDetails, onSubmit }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Prevent recording during system check
    const isSystemCheck = location.pathname.includes('/system-check');

    // Interview flow steps: 'photo' -> 'consent' -> 'introduction' -> 'interview'
    const [interviewStep, setInterviewStep] = useState<'photo' | 'consent' | 'introduction' | 'interview'>('photo');
    const [isCheckingState, setIsCheckingState] = useState(true); // Track if we're still checking backend state
    const hasCheckedStateRef = useRef(false); // Track if we've already checked backend state to prevent re-checking
    const interviewStepRef = useRef<'photo' | 'consent' | 'introduction' | 'interview'>('photo'); // Track current step to prevent reset
    const [photoCaptured, setPhotoCaptured] = useState(false);
    const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
    const [capturedPhotoUrl, setCapturedPhotoUrl] = useState<string | null>(null);
    const [consentRecorded, setConsentRecorded] = useState(false);
    const [introductionRecorded, setIntroductionRecorded] = useState(false);
    const [consentBlob, setConsentBlob] = useState<Blob | null>(null);
    const [introductionBlob, setIntroductionBlob] = useState<Blob | null>(null);
    const [candidateInfo, setCandidateInfo] = useState<{ firstName?: string; lastName?: string } | null>(null);
    const [consentVerificationFailed, setConsentVerificationFailed] = useState(false);
    const [introductionVerificationFailed, setIntroductionVerificationFailed] = useState(false);
    const consentAutoSubmitRef = useRef<boolean>(false); // Prevent multiple auto-submissions

    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isLoadingQuestionRef = useRef(false); // Prevent multiple simultaneous question loads
    const lastLoadedQuestionIdRef = useRef<string | null>(null); // Track last loaded question to prevent duplicates
    const timeUpHandledRef = useRef(false); // Track if time up has been handled to prevent multiple submissions
    const [isTimeUp, setIsTimeUp] = useState(false); // Track if time is up to disable interactions
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [answers, setAnswers] = useState<Array<{ question: string; recordingUrl: string; questionNumber: number; score?: number | null }>>([]);
    const [conversationHistory, setConversationHistory] = useState<Array<{ type: 'question' | 'answer'; content: string; questionNumber?: number; timestamp: Date }>>([]);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const answerInputRef = useRef<HTMLDivElement>(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [transcription, setTranscription] = useState<string>('');
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [answerSubmitted, setAnswerSubmitted] = useState(false);
    const [answerScore, setAnswerScore] = useState<number | null>(null);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [answerTimer, setAnswerTimer] = useState<number>(ANSWER_TIMER_DURATION);
    const [isAnswerTimerActive, setIsAnswerTimerActive] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [promptSpoken, setPromptSpoken] = useState(false);
    const answerTimerRef = useRef<NodeJS.Timeout | null>(null);
    const autoStartTimerRef = useRef<NodeJS.Timeout | null>(null);
    const noSpeechTimerRef = useRef<NodeJS.Timeout | null>(null);
    const transcriptionRef = useRef<string>('');
    const finalTranscriptsRef = useRef<string[]>([]); // Persist final transcripts across recognition restarts
    const interimTranscriptRef = useRef<string>(''); // Persist interim transcript across recognition restarts to prevent flickering
    const transcriptDisplayRef = useRef<HTMLDivElement | null>(null); // Ref for transcript display div for auto-scroll
    const isRecordingRef = useRef<boolean>(false);
    const wasRecordingBeforePromptRef = useRef<boolean>(false);
    const recognitionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null); // Interval to check if recognition is active

    const mediaRecorderRef = useRef<RecordRTC | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const interviewerVideoRef = useRef<HTMLVideoElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const recognitionRef = useRef<any>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const gainNodeRef = useRef<GainNode | null>(null);
    const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const destinationNodeRef = useRef<MediaStreamAudioDestinationNode | null>(null);

    // Function to scroll to answer input area
    const scrollToAnswerInput = useCallback(() => {
        setTimeout(() => {
            if (answerInputRef.current && chatContainerRef.current) {
                answerInputRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest'
                });
            } else if (chatContainerRef.current) {
                // Fallback: scroll to bottom
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
        }, 100);
    }, []);

    const loadNextQuestion = useCallback(async () => {
        if (!id) {
            console.error('No id available for loading question');
            return;
        }

        // Prevent multiple simultaneous loads
        if (isLoadingQuestionRef.current) {
            console.log('Question load already in progress, skipping...');
            return;
        }

        isLoadingQuestionRef.current = true;
        setIsLoading(true);
        try {
            const resData = await dispatch(getData(`/accel-test/ai-interview/question/${id}`));

            if (resData && resData.code === 200) {
                const data = decryptData(resData.data);

                if (data && data.question) {
                    // Check if this is the same question we just loaded
                    const questionId = data?.questionId || `${data?.questionNumber}-${data?.question}`;
                    // Only skip if it's the exact same question AND we still have it as current question
                    // This allows loading the next question after submission
                    if (lastLoadedQuestionIdRef.current === questionId && currentQuestion && currentQuestion.questionId === data.questionId) {
                        console.log('Same question returned from API, skipping...', questionId);
                        isLoadingQuestionRef.current = false;
                        setIsLoading(false);
                        return;
                    }

                    console.log('Loading new question:', {
                        questionNumber: data.questionNumber,
                        questionId: data.questionId,
                        previousQuestionId: lastLoadedQuestionIdRef.current
                    });

                    lastLoadedQuestionIdRef.current = questionId;
                    setCurrentQuestion(data);
                    setCurrentQuestionIndex(data.questionNumber - 1);

                    // Reset answer state when loading a new question
                    setAnswerSubmitted(false);
                    setRecordedBlob(null);
                    setTranscription('');
                    transcriptionRef.current = '';
                    finalTranscriptsRef.current = [];
                    interimTranscriptRef.current = '';

                    // Add question to conversation history
                    setConversationHistory(prev => {
                        // Check if this question is already in history
                        const exists = prev.some(msg =>
                            msg.type === 'question' &&
                            msg.content === data.question &&
                            msg.questionNumber === data.questionNumber
                        );
                        if (!exists) {
                            return [...prev, {
                                type: 'question' as const,
                                content: data.question,
                                questionNumber: data.questionNumber,
                                timestamp: new Date()
                            }];
                        }
                        return prev;
                    });

                    setTimeout(() => {
                        speakQuestion(data.question);
                    }, 500);
                } else {
                    // Check if there's a next stage or if test should be submitted
                    if (data?.nextStage) {
                        message.success('AI Interview completed! Moving to next section...');
                        setTimeout(() => {
                            // Call onSubmit callback to reload test details and update questionType
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                // Fallback navigation if no callback provided
                                if (data.nextStage === 'mcq') {
                                    navigate(`/online-exam/${id}/exam`);
                                } else if (data.nextStage === 'coding') {
                                    navigate(`/online-exam/${id}/exam`);
                                }
                            }
                        }, 1500);
                        return;
                    } else if (data?.shouldSubmitTest) {
                        message.success('All sections completed! Test submitted successfully.');
                        setTimeout(() => {
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                navigate(`/online-test/completed/${id}`);
                            }
                        }, 2000);
                        return;
                    } else {
                        // No more AI questions - check if there are coding questions or if test should be submitted
                        const hasCodingQuestions = testDetails?.coddingQuestions && testDetails.coddingQuestions.length > 0;
                        const totalCodingQuestions = testDetails?.totalCodingQuestions || 0;

                        if (hasCodingQuestions || totalCodingQuestions > 0) {
                            message.success('AI Interview completed! Moving to coding section...');
                            setTimeout(() => {
                                if (onSubmit) {
                                    onSubmit(); // This will reload test details and move to coding
                                } else {
                                    navigate(`/online-exam/${id}/exam`);
                                }
                            }, 1500);
                        } else {
                            message.success('All sections completed! Test submitted successfully.');
                            setTimeout(() => {
                                if (onSubmit) {
                                    onSubmit(); // This will handle test submission
                                } else {
                                    navigate(`/online-test/completed/${id}`);
                                }
                            }, 2000);
                        }
                        return;
                    }
                }
            } else if (resData && (resData.code === 404 || resData.status === 'ERROR')) {
                // No more questions available - check for next stage
                const errorMsg = resData?.message || resData?.data?.message || 'No more questions available';
                console.log('No more questions:', errorMsg);

                // Check if there are coding questions or if test should be submitted
                const hasCodingQuestions = testDetails?.coddingQuestions && testDetails.coddingQuestions.length > 0;
                const totalCodingQuestions = testDetails?.totalCodingQuestions || 0;

                if (hasCodingQuestions || totalCodingQuestions > 0) {
                    message.success('AI Interview completed! Moving to coding section...');
                    setTimeout(() => {
                        if (onSubmit) {
                            onSubmit(); // This will reload test details and move to coding
                        } else {
                            navigate(`/online-exam/${id}/exam`);
                        }
                    }, 1500);
                } else {
                    message.success('All sections completed! Test submitted successfully.');
                    setTimeout(() => {
                        if (onSubmit) {
                            onSubmit(); // This will handle test submission
                        } else {
                            navigate(`/online-test/completed/${id}`);
                        }
                    }, 2000);
                }
            } else {
                const errorMsg = resData?.message || resData?.data?.message || 'Failed to load question';
                console.error('Failed to load question:', errorMsg);
                message.error(errorMsg);
            }
        } catch (error) {
            console.error('Error loading question:', error);
            message.error('Error loading question. Please try again.');
        } finally {
            isLoadingQuestionRef.current = false;
            setIsLoading(false);
            scrollToAnswerInput();
        }
    }, [id, dispatch, navigate, onSubmit, scrollToAnswerInput, testDetails, currentQuestion]);

    // Initialize video stream once when component mounts
    useEffect(() => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.log('System check in progress - recording disabled');
            return;
        }

        const initializeVideoStream = async () => {
            try {
                // Request camera and microphone access once
                // Enhanced audio constraints for better low voice capture
                const originalStream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: false, // Disable auto gain to allow manual amplification
                        sampleRate: 48000, // Higher sample rate for better quality
                        channelCount: 1 // Mono for better compatibility
                    }
                });

                // Apply audio gain boost for low voice capture
                const stream = applyAudioGainBoost(originalStream);

                streamRef.current = stream;

                // Show video preview
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(err => {
                        console.error('Error playing video on mount:', err);
                    });
                }
            } catch (error) {
                console.error('Error initializing video stream:', error);
                message.error('Failed to access camera. Please grant camera permissions.');
            }
        };

        initializeVideoStream();

        return () => {
            // Only stop everything when component unmounts
            stopRecording();
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            // Stop any ongoing speech when component unmounts
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
            }
            // Stop speech recognition
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch (e) {
                    // Ignore errors
                }
                recognitionRef.current = null;
            }
            // Cleanup audio context and nodes
            if (sourceNodeRef.current) {
                try {
                    sourceNodeRef.current.disconnect();
                } catch (e) {
                    // Ignore errors
                }
                sourceNodeRef.current = null;
            }
            if (gainNodeRef.current) {
                try {
                    gainNodeRef.current.disconnect();
                } catch (e) {
                    // Ignore errors
                }
                gainNodeRef.current = null;
            }
            if (destinationNodeRef.current) {
                try {
                    destinationNodeRef.current.disconnect();
                } catch (e) {
                    // Ignore errors
                }
                destinationNodeRef.current = null;
            }
            if (audioContextRef.current) {
                try {
                    audioContextRef.current.close();
                } catch (e) {
                    // Ignore errors
                }
                audioContextRef.current = null;
            }
        };
    }, [isSystemCheck]);

    // Ensure video stream is set when entering interview step
    useEffect(() => {
        if (interviewStep === 'interview' && streamRef.current) {
            // Use a small delay to ensure video element is rendered
            const timer = setTimeout(() => {
                if (videoRef.current && streamRef.current) {
                    // Ensure video element has the stream
                    if (videoRef.current.srcObject !== streamRef.current) {
                        videoRef.current.srcObject = streamRef.current;
                    }

                    // Wait for metadata to load, then play
                    const playVideo = () => {
                        if (videoRef.current && !videoRef.current.paused) {
                            return; // Already playing
                        }
                        if (videoRef.current) {
                            videoRef.current.play().catch(err => {
                                console.error('Error playing video in interview step:', err);
                            });
                        }
                    };

                    if (videoRef.current.readyState >= 2) {
                        // Video metadata already loaded
                        playVideo();
                    } else {
                        // Wait for metadata
                        videoRef.current.onloadedmetadata = () => {
                            playVideo();
                        };
                    }
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [interviewStep]);

    // Update ref whenever interviewStep changes
    useEffect(() => {
        interviewStepRef.current = interviewStep;
    }, [interviewStep]);

    // Start/stop test monitoring
    useEffect(() => {
        if (id) {
            startTestMonitoring(id);
        }

        return () => {
            stopTestMonitoring();
        };
    }, [id]);

    // Check test link info and determine current step - only run once on mount or when id changes
    useEffect(() => {
        const checkTestLinkInfo = async () => {
            if (!id) {
                setIsCheckingState(false);
                return;
            }

            // Always check backend state on mount/reload
            setIsCheckingState(true);

            try {
                const resData = await dispatch(getData(`/accel-test/ai-interview/info/${id}`));
                if (resData && resData.code === 200) {
                    const data = decryptData(resData.data);

                    // Store candidate info
                    if (data.candidate) {
                        setCandidateInfo({
                            firstName: data.candidate.firstName,
                            lastName: data.candidate.lastName
                        });
                    }

                    // Load existing photo if already captured
                    const hasPhoto = !!(data.candidatePhotoUrl && data.candidatePhotoUrl.trim());
                    console.log('Checking backend state - hasPhoto:', hasPhoto, 'candidatePhotoUrl:', data.candidatePhotoUrl);
                    if (hasPhoto) {
                        setPhotoCaptured(true);
                        setCapturedPhotoUrl(data.candidatePhotoUrl);
                    }

                    // Load existing consent if already recorded
                    const hasConsent = !!(data.consentRecordingUrl && data.consentRecordingUrl.trim());
                    console.log('Checking backend state - hasConsent:', hasConsent, 'consentRecordingUrl:', data.consentRecordingUrl);
                    if (hasConsent) {
                        setConsentRecorded(true);
                    }

                    // Load existing introduction if already recorded
                    const hasIntroduction = !!(data.introductionRecordingUrl && data.introductionRecordingUrl.trim());
                    console.log('Checking backend state - hasIntroduction:', hasIntroduction, 'introductionRecordingUrl:', data.introductionRecordingUrl);
                    if (hasIntroduction) {
                        setIntroductionRecorded(true);
                    }

                    // Determine the correct step based on what's completed
                    // Always go to the first pending step, skip completed ones
                    let nextStep: 'photo' | 'consent' | 'introduction' | 'interview' = 'photo';

                    if (hasPhoto && hasConsent && hasIntroduction) {
                        // All steps complete, go directly to interview
                        nextStep = 'interview';
                        console.log('All steps complete, going to interview');
                    } else if (hasPhoto && hasConsent) {
                        // Photo and consent done, go to introduction
                        nextStep = 'introduction';
                        console.log('Photo and consent done, going to introduction');
                    } else if (hasPhoto) {
                        // Only photo done, go to consent
                        nextStep = 'consent';
                        console.log('Only photo done, going to consent');
                    } else {
                        // Nothing done, start with photo
                        nextStep = 'photo';
                        console.log('Nothing done, starting with photo');
                    }

                    // Set the step
                    setInterviewStep(nextStep);
                    interviewStepRef.current = nextStep;
                }
            } catch (error) {
                console.error('Error checking test link info:', error);
                // On error, default to photo step
                setInterviewStep('photo');
                interviewStepRef.current = 'photo';
            } finally {
                setIsCheckingState(false);
            }
        };
        checkTestLinkInfo();
    }, [id, dispatch]); // Always run on mount or when id changes

    // Ensure video stream is attached when consent or introduction step is active
    useEffect(() => {
        if ((interviewStep === 'consent' || interviewStep === 'introduction') && streamRef.current && videoRef.current) {
            // Ensure video element has the stream attached
            if (videoRef.current.srcObject !== streamRef.current) {
                videoRef.current.srcObject = streamRef.current;
                videoRef.current.play().catch(err => {
                    console.error('Error playing video:', err);
                });
            }
        }
    }, [interviewStep]);

    // Load first question when interview step is active
    useEffect(() => {
        if (interviewStep === 'interview' && id && !currentQuestion && !isLoadingQuestionRef.current) {
            console.log('Interview step active, loading first question');
            loadNextQuestion();
        }
    }, [interviewStep, id]); // Removed loadNextQuestion and currentQuestion from dependencies

    // Auto-read question and start timer when it changes
    useEffect(() => {
        if (currentQuestion?.question) {
            // Reset state when new question loads
            setTranscription('');
            transcriptionRef.current = '';
            finalTranscriptsRef.current = [];
            interimTranscriptRef.current = '';
            setRecordedBlob(null);
            setAnswerSubmitted(false);
            setAnswerScore(null);
            setIsRecording(false);
            setIsEvaluating(false);
            setRecordingTime(0);
            setAnswerTimer(ANSWER_TIMER_DURATION);
            setIsAnswerTimerActive(false);
            setIsTimeUp(false); // Reset time up state when new question loads
            timeUpHandledRef.current = false; // Reset time up handler ref

            // Stop any ongoing recording and timers
            if (mediaRecorderRef.current) {
                stopRecording();
            }
            if (answerTimerRef.current) {
                clearInterval(answerTimerRef.current);
                answerTimerRef.current = null;
            }
            if (autoStartTimerRef.current) {
                clearTimeout(autoStartTimerRef.current);
            }

            scrollToAnswerInput();

            const timer = setTimeout(() => {
                speakQuestion(currentQuestion.question, () => {
                    setIsAnswerTimerActive(true);
                    setAnswerTimer(ANSWER_TIMER_DURATION);

                    answerTimerRef.current = setInterval(() => {
                        setAnswerTimer(prev => {
                            const newTime = prev - 1;
                            if (newTime <= 0) {
                                if (answerTimerRef.current) {
                                    clearInterval(answerTimerRef.current);
                                    answerTimerRef.current = null;
                                }
                                setIsAnswerTimerActive(false);
                                setAnswerTimer(0);

                                if (isRecording && mediaRecorderRef.current) {
                                    stopRecording();
                                }

                                message.warning('Time ended! Auto-submitting your answer...', 2);

                                setTimeout(() => {
                                    autoSaveAnswer();
                                }, 1500);

                                return 0;
                            }
                            return newTime;
                        });
                    }, 1000);

                    setTimeout(() => {
                        startRecording();
                    }, 500);
                });
            }, 800);

            return () => {
                clearTimeout(timer);
                if (autoStartTimerRef.current) {
                    clearTimeout(autoStartTimerRef.current);
                }
                if (answerTimerRef.current) {
                    clearInterval(answerTimerRef.current);
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion?.question, scrollToAnswerInput]);

    // Auto-scroll to answer input when transcription changes
    useEffect(() => {
        if (transcription && !answerSubmitted) {
            scrollToAnswerInput();
        }
    }, [transcription, answerSubmitted, scrollToAnswerInput]);

    // Auto-scroll transcript display to bottom when transcription updates (smooth scrolling)
    useEffect(() => {
        if (transcriptDisplayRef.current && transcription) {
            // Use requestAnimationFrame to ensure DOM is updated before scrolling
            requestAnimationFrame(() => {
                if (transcriptDisplayRef.current) {
                    transcriptDisplayRef.current.scrollTo({
                        top: transcriptDisplayRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            });
        }
    }, [transcription]);


    // Initialize Speech Recognition with improved accuracy settings (declared first to avoid circular dependency)
    const initializeSpeechRecognition = useCallback(() => {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            console.warn('Speech recognition not supported in this browser');
            message.warning('Speech recognition is not supported in your browser. Please use Chrome or Edge for best results.');
            return null;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        // Improved settings for better accuracy
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 1; // Get best match only

        // IMPORTANT: Don't reset finalTranscriptsRef - preserve existing transcripts
        // This ensures we don't lose transcribed text when recognition restarts

        // Use ref to persist final transcripts across recognition restarts
        // This ensures we don't lose transcribed text when recognition restarts

        recognition.onresult = (event: any) => {
            let newInterimTranscript = '';
            let hasNewFinal = false;

            // Process only new results (from resultIndex onwards)
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                const transcript = result[0].transcript.trim();
                const confidence = result[0].confidence || 0;

                // Log confidence for debugging
                if (confidence < 0.5) {
                    console.warn(`Low confidence transcription: "${transcript}" (confidence: ${confidence})`);
                }

                if (result.isFinal) {
                    hasNewFinal = true;
                    // Only add if it's a new final transcript (avoid duplicates)
                    // Check against current final transcripts in ref
                    if (transcript && !finalTranscriptsRef.current.includes(transcript)) {
                        finalTranscriptsRef.current.push(transcript);
                        console.log(`Final transcript added: "${transcript}" (confidence: ${confidence}). Total final transcripts: ${finalTranscriptsRef.current.length}`);
                    }
                } else {
                    // Interim results - accumulate new interim transcript
                    newInterimTranscript += transcript + ' ';
                }
            }

            // Update interim transcript ref
            // If we have new interim results, replace the stored interim transcript
            // If we have new final results, clear the interim transcript (since it's now final)
            if (hasNewFinal) {
                // Clear interim transcript when we get final results (they're now in finalTranscriptsRef)
                interimTranscriptRef.current = '';
            } else if (newInterimTranscript.trim()) {
                // Update with new interim transcript, preserving any existing interim text
                interimTranscriptRef.current = newInterimTranscript.trim();
            }

            setTranscription(prev => {
                // Build transcription from all final transcripts (persisted in ref) + current/new interim
                const allFinalText = finalTranscriptsRef.current.join(' ');
                // Use new interim if available, otherwise use stored interim to prevent flickering
                const currentInterim = newInterimTranscript.trim() || interimTranscriptRef.current;
                const newText = currentInterim
                    ? (allFinalText + ' ' + currentInterim).trim()
                    : allFinalText.trim();

                // Don't remove duplicates - preserve all words to avoid losing content
                // The deduplication was too aggressive and could remove valid repeated words
                const finalText = newText;

                // Update ref with full text
                transcriptionRef.current = finalText;

                // If transcription appears, clear the no-speech timer
                if (finalText && finalText.trim() !== '' && noSpeechTimerRef.current) {
                    clearTimeout(noSpeechTimerRef.current);
                    noSpeechTimerRef.current = null;
                }

                return finalText;
            });
        };

        recognition.onerror = (event: any) => {
            console.error('Speech recognition error:', event.error);
            if (event.error === 'no-speech') {
                // This is normal, just means no speech detected yet
                // Recognition will automatically restart via onend handler
                console.log('No speech detected - recognition will restart automatically...');
            } else if (event.error === 'audio-capture') {
                message.error('Microphone not found or not accessible. Please check your microphone settings.');
            } else if (event.error === 'not-allowed') {
                message.error('Microphone permission denied. Please allow microphone access and refresh the page.');
            } else if (event.error === 'network') {
                message.warning('Network error in speech recognition. Please check your internet connection.');
            } else if (event.error === 'aborted') {
                console.log('Speech recognition aborted');
            } else {
                message.warning(`Speech recognition error: ${event.error}. Transcription may be inaccurate.`);
            }
        };

        recognition.onend = () => {
            // Always restart recognition immediately if still recording (even after no-speech timeout)
            // Use refs to check current state since state might not be updated
            if (isRecordingRef.current && isRecording) {
                // Restart immediately without delay to capture speech as soon as user starts speaking
                // Double-check that we're still recording
                if (isRecordingRef.current && isRecording) {
                    // Check if recognition instance still exists, if not create a new one
                    if (!recognitionRef.current) {
                        console.log('Recognition instance lost, reinitializing...');
                        const newRecognition = initializeSpeechRecognition();
                        if (newRecognition) {
                            recognitionRef.current = newRecognition;
                            try {
                                newRecognition.start();
                                setIsTranscribing(true);
                                console.log('Speech recognition restarted immediately (new instance)');
                            } catch (e) {
                                console.log('Error restarting new recognition:', e);
                                // Retry after a very short delay if immediate start fails
                                setTimeout(() => {
                                    if (isRecordingRef.current && isRecording) {
                                        try {
                                            newRecognition.start();
                                            setIsTranscribing(true);
                                        } catch (e2) {
                                            console.log('Error on retry:', e2);
                                        }
                                    }
                                }, 50);
                            }
                        }
                    } else {
                        // Use existing recognition instance - restart immediately
                        try {
                            recognitionRef.current.start();
                            setIsTranscribing(true);
                            console.log('Speech recognition restarted immediately after onend');
                        } catch (e) {
                            console.log('Error restarting recognition:', e);
                            // If restart fails, try reinitializing
                            const newRecognition = initializeSpeechRecognition();
                            if (newRecognition) {
                                recognitionRef.current = newRecognition;
                                try {
                                    newRecognition.start();
                                    setIsTranscribing(true);
                                    console.log('Speech recognition reinitialized and started');
                                } catch (e2) {
                                    console.log('Error starting reinitialized recognition:', e2);
                                    // Final retry after short delay
                                    setTimeout(() => {
                                        if (isRecordingRef.current && isRecording) {
                                            try {
                                                newRecognition.start();
                                                setIsTranscribing(true);
                                            } catch (e3) {
                                                console.log('Error on final retry:', e3);
                                            }
                                        }
                                    }, 50);
                                }
                            }
                        }
                    }
                }
            } else {
                console.log('Recognition ended but not recording, not restarting');
            }
        };

        return recognition;
    }, [isRecording]);

    // Helper function to restart recognition after system speech
    const restartRecognitionAfterSpeech = useCallback(() => {
        // Check if we're still recording
        if (!isRecordingRef.current || !mediaRecorderRef.current) {
            console.log('Not recording anymore, skipping recognition restart');
            return;
        }

        // If recognition instance exists, try to restart it
        if (recognitionRef.current) {
            try {
                recognitionRef.current.start();
                setIsTranscribing(true);
                console.log('Recognition restarted after system speech');
            } catch (e) {
                console.log('Error restarting existing recognition, reinitializing...', e);
                // If restart fails, create a new recognition instance
                const newRecognition = initializeSpeechRecognition();
                if (newRecognition) {
                    recognitionRef.current = newRecognition;
                    try {
                        newRecognition.start();
                        setIsTranscribing(true);
                        console.log('New recognition instance started after system speech');
                    } catch (e2) {
                        console.error('Error starting new recognition instance:', e2);
                    }
                }
            }
        } else {
            // No recognition instance, create a new one
            const newRecognition = initializeSpeechRecognition();
            if (newRecognition) {
                recognitionRef.current = newRecognition;
                try {
                    newRecognition.start();
                    setIsTranscribing(true);
                    console.log('New recognition instance created and started after system speech');
                } catch (e) {
                    console.error('Error starting new recognition instance:', e);
                }
            }
        }
    }, [initializeSpeechRecognition]);

    // Text-to-speech function
    const speakQuestion = useCallback((text: string, onComplete?: () => void) => {
        // Check if recording was active before speaking
        const wasRecording = isRecordingRef.current && mediaRecorderRef.current !== null;
        wasRecordingBeforePromptRef.current = wasRecording;

        // Stop speech recognition first to prevent transcribing system speech
        // But DON'T stop the actual recording (RecordRTC) - keep it running
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
                setIsTranscribing(false);
            } catch (e) {
                console.log('Error stopping recognition before speaking:', e);
            }
        }

        // Clear any transcription that might have been captured during system speech
        setTranscription('');
        transcriptionRef.current = '';
        interimTranscriptRef.current = '';

        if ('speechSynthesis' in window) {
            // Stop any ongoing speech
            window.speechSynthesis.cancel();

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = SPEECH_CONFIG.rate;
            utterance.pitch = SPEECH_CONFIG.pitch;
            utterance.volume = SPEECH_CONFIG.volume;
            utterance.lang = SPEECH_CONFIG.lang;

            utterance.onstart = () => {
                setIsSpeaking(true);
            };

            utterance.onerror = (event) => {
                console.error('Speech synthesis error:', event);
                setIsSpeaking(false);
                // Restart recognition if recording was active
                if (wasRecordingBeforePromptRef.current && isRecordingRef.current) {
                    restartRecognitionAfterSpeech();
                }
                if (onComplete) onComplete();
            };

            utterance.onend = () => {
                console.log('Question speech completed');
                setIsSpeaking(false);

                // Wait a bit longer to ensure system speech is completely finished
                // before restarting recognition
                setTimeout(() => {
                    // Restart recognition if recording was active before the prompt
                    if (wasRecordingBeforePromptRef.current && isRecordingRef.current) {
                        wasRecordingBeforePromptRef.current = false;
                        restartRecognitionAfterSpeech();
                    }
                }, 800); // Longer delay to ensure speech is fully finished

                if (onComplete) onComplete();
            };

            window.speechSynthesis.speak(utterance);
        } else {
            // If speech synthesis not available, call onComplete immediately
            setIsSpeaking(false);
            if (wasRecordingBeforePromptRef.current && isRecordingRef.current) {
                restartRecognitionAfterSpeech();
            }
            wasRecordingBeforePromptRef.current = false;
            if (onComplete) {
                setTimeout(onComplete, 1000);
            }
        }
    }, [restartRecognitionAfterSpeech]);

    // Speak introduction prompt when user reaches introduction step
    useEffect(() => {
        if (interviewStep === 'introduction' && !isRecording && !introductionBlob) {
            // Wait a bit for the UI to render, then speak the prompt
            const timer = setTimeout(() => {
                const introductionPrompt = "Please introduce yourself. Share your name, background, experience, and why you're interested in this role. When you're ready, click the Start Recording button.";
                speakQuestion(introductionPrompt);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [interviewStep, isRecording, introductionBlob, speakQuestion]);

    // Helper function to apply audio gain boost for low voice capture
    const applyAudioGainBoost = useCallback((stream: MediaStream): MediaStream => {
        try {
            // Create AudioContext if it doesn't exist
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            }

            const audioContext = audioContextRef.current;

            // Get audio track from stream
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length === 0) {
                console.warn('No audio tracks found in stream');
                return stream;
            }

            // Create source node from stream
            if (sourceNodeRef.current) {
                sourceNodeRef.current.disconnect();
            }
            sourceNodeRef.current = audioContext.createMediaStreamSource(stream);

            // Create destination node for output stream
            if (destinationNodeRef.current) {
                destinationNodeRef.current.disconnect();
            }
            destinationNodeRef.current = audioContext.createMediaStreamDestination();

            // Create gain node with boost (2.0 = 2x amplification, 3.0 = 3x, etc.)
            if (gainNodeRef.current) {
                gainNodeRef.current.disconnect();
            }
            gainNodeRef.current = audioContext.createGain();
            gainNodeRef.current.gain.value = 3.5; // Boost audio by 3.5x for better low voice capture

            // Connect: source -> gain -> destination
            sourceNodeRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(destinationNodeRef.current);

            // Get video tracks from original stream
            const videoTracks = stream.getVideoTracks();

            // Create new stream with boosted audio and original video
            const boostedStream = new MediaStream();
            videoTracks.forEach(track => boostedStream.addTrack(track));
            destinationNodeRef.current.stream.getAudioTracks().forEach(track => {
                boostedStream.addTrack(track);
            });

            console.log('Audio gain boost applied (3.5x amplification)');
            return boostedStream;
        } catch (error) {
            console.error('Error applying audio gain boost:', error);
            // Return original stream if gain boost fails
            return stream;
        }
    }, []);

    const startRecording = useCallback(async () => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Recording blocked: System check in progress');
            message.warning('Recording is disabled during system check');
            return;
        }

        // Prevent recording during introduction if system is still speaking
        if (interviewStep === 'introduction' && isSpeaking) {
            message.warning('Please wait for the system to finish speaking before starting your recording.');
            return;
        }

        try {
            // Reuse existing stream if available, otherwise request new one
            let stream = streamRef.current;

            if (!stream) {
                // Request camera and microphone access if stream doesn't exist
                // Enhanced audio constraints for better low voice capture
                const originalStream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: false, // Disable auto gain to allow manual amplification
                        sampleRate: 48000, // Higher sample rate for better quality
                        channelCount: 1 // Mono for better compatibility
                    }
                });

                // Apply audio gain boost for low voice capture
                stream = applyAudioGainBoost(originalStream);
                streamRef.current = stream;

                // Show video preview
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
            } else {
                // Stream exists - ensure audio context is active and gain boost is still working
                // Check if audio tracks are active
                const audioTracks = stream.getAudioTracks();
                const hasActiveAudio = audioTracks.length > 0 && audioTracks.some(track => track.readyState === 'live');

                if (!hasActiveAudio) {
                    console.warn('Audio tracks are not active, requesting new stream');
                    // Request new stream if tracks are inactive
                    const originalStream = await navigator.mediaDevices.getUserMedia({
                        video: true,
                        audio: {
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: false,
                            sampleRate: 48000,
                            channelCount: 1
                        }
                    });
                    stream = applyAudioGainBoost(originalStream);
                    streamRef.current = stream;
                } else if (audioContextRef.current) {
                    // Resume audio context if suspended (required for user interaction)
                    if (audioContextRef.current.state === 'suspended') {
                        await audioContextRef.current.resume();
                        console.log('Audio context resumed for recording');
                    }

                    // Verify and restore gain node value
                    if (gainNodeRef.current) {
                        // Always ensure gain value is set correctly (may have been reset)
                        if (gainNodeRef.current.gain.value !== 3.5) {
                            gainNodeRef.current.gain.value = 3.5;
                            console.log('Gain node value restored to 3.5x');
                        }
                    } else {
                        // Gain node is missing - this shouldn't happen with existing stream
                        // But if it does, the stream should still have boosted audio from destination
                        console.warn('Gain node missing but stream exists - stream should still have boosted audio');
                    }
                }
            }

            // Start recording with RecordRTC - audio only for better compatibility
            const recorder = new RecordRTC(stream, RECORDING_CONFIG);

            recorder.startRecording();
            mediaRecorderRef.current = recorder;

            setIsRecording(true);
            isRecordingRef.current = true;
            setRecordingTime(0);

            // Clear previous blobs based on current step
            if (interviewStep === 'consent') {
                setConsentBlob(null);
            } else if (interviewStep === 'introduction') {
                setIntroductionBlob(null);
            } else {
                setRecordedBlob(null);
            }

            setTranscription(''); // Reset transcription
            transcriptionRef.current = '';
            finalTranscriptsRef.current = []; // Reset final transcripts for new recording session
            interimTranscriptRef.current = ''; // Reset interim transcript for new recording session
            setPromptSpoken(false); // Reset prompt flag
            consentAutoSubmitRef.current = false; // Reset auto-submit flag for consent
            setConsentVerificationFailed(false); // Reset verification failure flag

            // Start speech recognition for transcription (for interview, introduction, and consent steps)
            if (interviewStep === 'interview' || interviewStep === 'introduction' || interviewStep === 'consent') {
                const recognition = initializeSpeechRecognition();
                if (recognition) {
                    recognitionRef.current = recognition;
                    try {
                        recognition.start();
                        setIsTranscribing(true);
                        console.log('Speech recognition started');
                    } catch (e) {
                        console.log('Speech recognition start error:', e);
                        setIsTranscribing(false);
                    }
                }
            }

            // Start monitoring to ensure recognition stays active
            // Check every 2 seconds if recognition is running and restart if needed
            recognitionCheckIntervalRef.current = setInterval(() => {
                if (isRecordingRef.current && (interviewStep === 'interview' || interviewStep === 'introduction' || interviewStep === 'consent')) {
                    // Check if recognition exists and is not running
                    if (!recognitionRef.current) {
                        console.log('Recognition instance missing, reinitializing...');
                        const newRecognition = initializeSpeechRecognition();
                        if (newRecognition) {
                            recognitionRef.current = newRecognition;
                            try {
                                newRecognition.start();
                                setIsTranscribing(true);
                                console.log('Recognition restarted by monitor');
                            } catch (e) {
                                console.log('Error restarting recognition in monitor:', e);
                            }
                        }
                    } else {
                        // Check if recognition is in a stopped state and restart if needed
                        // Note: We can't directly check if recognition is running, but we can try to restart it
                        // If it's already running, start() will throw an error which we'll catch
                        try {
                            recognitionRef.current.start();
                            setIsTranscribing(true);
                            console.log('Recognition restarted by monitor (was stopped)');
                        } catch (e: any) {
                            // If error is "already started" or similar, that's fine - recognition is running
                            if (e.message && e.message.includes('already')) {
                                // Recognition is already running, which is good
                            } else {
                                console.log('Recognition check - may need restart:', e);
                            }
                        }
                    }
                }
            }, 2000); // Check every 2 seconds

            // Start recording timer (no limit - controlled by answer timer)
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

            // Check after 20 seconds if no transcription detected
            noSpeechTimerRef.current = setTimeout(() => {
                // Check current state using refs
                if (isRecordingRef.current && (!transcriptionRef.current || transcriptionRef.current.trim() === '') && !promptSpoken) {
                    speakQuestion('Can you please speak louder? I am unable to hear you.');
                    setPromptSpoken(true);
                }
            }, NO_SPEECH_TIMEOUT);

        } catch (error) {
            console.error('Error starting recording:', error);
            message.error('Failed to start recording. Please allow camera and microphone access.');
        }
    }, [interviewStep, isSpeaking, initializeSpeechRecognition, speakQuestion, isSystemCheck]);

    // Save candidate photo
    const saveCandidatePhoto = useCallback(async (photoBlob: Blob) => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Photo save blocked: System check in progress');
            message.warning('Photo capture is disabled during system check');
            return;
        }

        if (!id) return;
        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('photo', photoBlob, 'candidate-photo.jpg');

            // uploadFile is a regular async function, not a Redux action, so call it directly
            const resData: any = await uploadFile(`/accel-test/ai-interview/photo/${id}`, formData);
            if (resData && resData.code === 200) {
                message.success('Photo captured successfully');
                setPhotoCaptured(true);
                setPhotoBlob(null);
                setCapturedPhotoUrl(null);
                setInterviewStep('consent');
            } else {
                const errorMsg = resData?.message || 'Failed to save photo';
                message.error(errorMsg);
            }
        } catch (error: any) {
            console.error('Error saving candidate photo:', error);
            message.error(error?.message || 'Failed to save photo');
        } finally {
            setIsSubmitting(false);
        }
    }, [id]);

    // Save consent recording
    const saveConsentRecording = useCallback(async (blob: Blob, transcriptText?: string) => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Recording save blocked: System check in progress');
            message.warning('Recording is disabled during system check');
            return;
        }

        if (!id) return;

        // Validate blob exists
        if (!blob || blob.size === 0) {
            message.error('No recording found. Please record the consent statement first.');
            return;
        }

        // Validate transcript length
        const completeTranscription = transcriptText || transcriptionRef.current || transcription || '';
        const finalTranscription = completeTranscription.trim();

        if (!finalTranscription || finalTranscription.length < MIN_CONSENT_TRANSCRIPT_LENGTH) {
            message.error(`Transcript is too short. Please read the complete consent statement (at least ${MIN_CONSENT_TRANSCRIPT_LENGTH} characters).`);
            setConsentVerificationFailed(true);
            return;
        }

        // Verify consent statement similarity
        const consentStatement = `By proceeding with this interview, I voluntarily provide my explicit consent to have my voice recorded during this AI-powered interview session. I understand that my audio responses will be recorded, processed, and analyzed by an automated AI system for the purpose of interview evaluation, scoring, and recruitment assessment.`;
        const similarity = calculateTextSimilarity(finalTranscription, consentStatement);
        console.log(`Consent verification - Similarity: ${(similarity * 100).toFixed(1)}%, Transcript length: ${finalTranscription.length}`);

        if (similarity < 0.60) {
            message.error('The consent statement does not match. Please read the complete statement again clearly.');
            setConsentVerificationFailed(true);
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('recording', blob, 'consent.webm');
            formData.append('transcript', finalTranscription);

            // uploadFile is a regular async function, not a Redux action, so call it directly
            const resData: any = await uploadFile(`/accel-test/ai-interview/consent/${id}`, formData);
            if (resData && resData.code === 200) {
                message.success('Consent recorded successfully');
                setConsentRecorded(true);
                setConsentBlob(null);
                setConsentVerificationFailed(false);
                setInterviewStep('introduction');
            } else {
                const errorMsg = resData?.message || 'Failed to save consent recording';
                message.error(errorMsg);
                setConsentVerificationFailed(true);
            }
        } catch (error: any) {
            console.error('Error saving consent recording:', error);
            message.error(error?.message || 'Failed to save consent recording');
            setConsentVerificationFailed(true);
        } finally {
            setIsSubmitting(false);
        }
    }, [id, transcription, transcriptionRef, isSystemCheck]);

    // Stop recording and automatically submit for consent
    const stopAndSubmitConsent = useCallback(() => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Recording stop blocked: System check in progress');
            message.warning('Recording is disabled during system check');
            return;
        }

        if (!mediaRecorderRef.current) {
            console.warn('No media recorder found');
            message.error('No recording found. Please start recording first.');
            return;
        }

        // Check if recording time is too short (less than 5 seconds for consent)
        if (recordingTime < MIN_RECORDING_TIME) {
            message.warning(`Recording is too short. Please record for at least ${MIN_RECORDING_TIME} seconds.`);
            return;
        }

        // Verify consent statement similarity (unless auto-submitted)
        const consentStatement = `By proceeding with this interview, I voluntarily provide my explicit consent to have my voice recorded during this AI-powered interview session. I understand that my audio responses will be recorded, processed, and analyzed by an automated AI system for the purpose of interview evaluation, scoring, and recruitment assessment.`;
        const completeTranscription = transcriptionRef.current || transcription || '';
        const finalTranscription = completeTranscription.trim();

        // Check if transcript exists and meets minimum length requirement
        if (!finalTranscription || finalTranscription.length < MIN_CONSENT_TRANSCRIPT_LENGTH) {
            setConsentVerificationFailed(true);
            message.warning(`Transcript is too short. Please read the complete consent statement (at least ${MIN_CONSENT_TRANSCRIPT_LENGTH} characters).`);

            // Stop recording but don't submit
            setIsRecording(false);
            isRecordingRef.current = false;

            // Stop timers
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (noSpeechTimerRef.current) {
                clearTimeout(noSpeechTimerRef.current);
                noSpeechTimerRef.current = null;
            }
            if (recognitionCheckIntervalRef.current) {
                clearInterval(recognitionCheckIntervalRef.current);
                recognitionCheckIntervalRef.current = null;
            }

            // Stop speech recognition
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch (e) {
                    console.log('Error stopping recognition:', e);
                }
                setIsTranscribing(false);
            }

            // Stop media recorder
            try {
                const recorder = mediaRecorderRef.current;
                mediaRecorderRef.current = null;
                recorder.stopRecording(() => {
                    const blob = recorder.getBlob();
                    if (blob && blob.size > 0) {
                        setConsentBlob(blob);
                    }
                });
            } catch (error) {
                console.error('Error stopping recording:', error);
            }

            return; // Don't proceed with submission
        }

        if (finalTranscription && !consentAutoSubmitRef.current) {
            const similarity = calculateTextSimilarity(finalTranscription, consentStatement);
            console.log(`Consent verification - Similarity: ${(similarity * 100).toFixed(1)}%, Transcript length: ${finalTranscription.length}`);

            if (similarity < 0.60) {
                // Similarity is too low, ask user to read again
                setConsentVerificationFailed(true);
                message.warning('The consent statement does not match. Please read the complete statement again clearly.');

                // Stop recording but don't submit
                setIsRecording(false);
                isRecordingRef.current = false;

                // Stop timers
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
                if (noSpeechTimerRef.current) {
                    clearTimeout(noSpeechTimerRef.current);
                    noSpeechTimerRef.current = null;
                }
                if (recognitionCheckIntervalRef.current) {
                    clearInterval(recognitionCheckIntervalRef.current);
                    recognitionCheckIntervalRef.current = null;
                }

                // Stop speech recognition
                if (recognitionRef.current) {
                    try {
                        recognitionRef.current.stop();
                    } catch (e) {
                        console.log('Error stopping recognition:', e);
                    }
                    setIsTranscribing(false);
                }

                // Stop media recorder
                try {
                    const recorder = mediaRecorderRef.current;
                    mediaRecorderRef.current = null;
                    recorder.stopRecording(() => {
                        const blob = recorder.getBlob();
                        if (blob && blob.size > 0) {
                            setConsentBlob(blob);
                        }
                    });
                } catch (error) {
                    console.error('Error stopping recording:', error);
                }

                return; // Don't proceed with submission
            }
        }

        // Stop timers first
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (noSpeechTimerRef.current) {
            clearTimeout(noSpeechTimerRef.current);
            noSpeechTimerRef.current = null;
        }
        if (recognitionCheckIntervalRef.current) {
            clearInterval(recognitionCheckIntervalRef.current);
            recognitionCheckIntervalRef.current = null;
        }

        setIsRecording(false);
        isRecordingRef.current = false;

        try {
            const recorder = mediaRecorderRef.current;
            mediaRecorderRef.current = null;

            // Capture recording time before async operations
            const currentRecordingTime = recordingTime;

            recorder.stopRecording(() => {
                try {
                    // Wait a bit for the blob to be fully processed
                    setTimeout(() => {
                        const blob = recorder.getBlob();
                        console.log('Consent recording blob:', blob ? `size: ${blob.size}, type: ${blob.type}` : 'null');

                        if (blob && blob.size > 0) {
                            console.log('Stopping and submitting consent, blob size:', blob.size);

                            // Get complete transcription before submitting
                            const completeTranscription = transcriptionRef.current || transcription || '';
                            const finalTranscription = completeTranscription.trim();

                            // Validate recording time and transcript before submitting
                            if (currentRecordingTime < MIN_RECORDING_TIME) {
                                message.error(`Recording is too short. Please record for at least ${MIN_RECORDING_TIME} seconds.`);
                                setConsentBlob(blob);
                                setConsentVerificationFailed(true);
                                return;
                            }

                            if (!finalTranscription || finalTranscription.length < MIN_CONSENT_TRANSCRIPT_LENGTH) {
                                message.error(`Transcript is too short. Please read the complete consent statement (at least ${MIN_CONSENT_TRANSCRIPT_LENGTH} characters).`);
                                setConsentBlob(blob);
                                setConsentVerificationFailed(true);
                                return;
                            }

                            // Verify similarity before submitting
                            const consentStatement = `By proceeding with this interview, I voluntarily provide my explicit consent to have my voice recorded during this AI-powered interview session. I understand that my audio responses will be recorded, processed, and analyzed by an automated AI system for the purpose of interview evaluation, scoring, and recruitment assessment.`;
                            const similarity = calculateTextSimilarity(finalTranscription, consentStatement);

                            if (similarity < 0.60) {
                                message.error('The consent statement does not match. Please read the complete statement again clearly.');
                                setConsentBlob(blob);
                                setConsentVerificationFailed(true);
                                return;
                            }

                            // All validations passed, submit
                            setConsentBlob(blob);
                            saveConsentRecording(blob, finalTranscription);
                        } else {
                            console.error('No blob received from recorder or blob is empty', {
                                blob: blob,
                                size: blob?.size,
                                type: blob?.type
                            });
                            message.error('Recording failed. The recording may be too short or there was an error. Please try again.');
                            setConsentVerificationFailed(true);
                        }
                    }, BLOB_PROCESSING_DELAY);
                } catch (error) {
                    console.error('Error in stopAndSubmitConsent callback:', error);
                    message.error('Error processing recording. Please try again.');
                    setConsentVerificationFailed(true);
                }
            });
        } catch (error) {
            console.error('Error stopping recording:', error);
            message.error('Error stopping recording. Please try again.');
        }
    }, [recordingTime, saveConsentRecording, transcription, transcriptionRef, isSystemCheck]);

    // Monitor consent transcription and auto-submit when it matches
    useEffect(() => {
        // Prevent recording during system check
        if (isSystemCheck) {
            return;
        }

        if (interviewStep === 'consent' && isRecording && transcription && !consentAutoSubmitRef.current) {
            const consentStatement = `By proceeding with this interview, I voluntarily provide my explicit consent to have my voice recorded during this AI-powered interview session. I understand that my audio responses will be recorded, processed, and analyzed by an automated AI system for the purpose of interview evaluation, scoring, and recruitment assessment.`;

            // Get complete transcription
            const completeTranscription = transcriptionRef.current || transcription || '';
            const finalTranscription = completeTranscription.trim();

            // Check minimum transcript length
            if (finalTranscription.length < MIN_CONSENT_TRANSCRIPT_LENGTH) {
                return; // Wait for more transcription
            }

            // Check minimum recording time
            if (recordingTime < MIN_RECORDING_TIME) {
                return; // Wait for minimum recording time
            }

            // Calculate similarity
            const similarity = calculateTextSimilarity(finalTranscription, consentStatement);

            // If similarity is high enough (60% or more), auto-submit
            if (similarity >= 0.60) {
                console.log(`Consent transcription matches! Similarity: ${(similarity * 100).toFixed(1)}%, Length: ${finalTranscription.length}, Time: ${recordingTime}s`);
                consentAutoSubmitRef.current = true; // Prevent multiple submissions

                // Wait a moment to ensure transcription is complete, then auto-submit
                setTimeout(() => {
                    if (isRecording && mediaRecorderRef.current && recordingTime >= MIN_RECORDING_TIME) {
                        message.success('Consent statement verified! Submitting automatically...');
                        stopAndSubmitConsent();
                    }
                }, 1000); // 1 second delay to ensure final transcription is captured
            }
        }
    }, [interviewStep, isRecording, transcription, recordingTime, stopAndSubmitConsent, isSystemCheck, transcriptionRef]);

    // Save introduction recording
    const saveIntroductionRecording = useCallback(async (blob: Blob, transcriptText?: string) => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Recording save blocked: System check in progress');
            message.warning('Recording is disabled during system check');
            return;
        }

        if (!id) return;

        // Validate blob exists
        if (!blob || blob.size === 0) {
            message.error('No recording found. Please record your introduction first.');
            return;
        }

        // Get complete transcription
        const completeTranscription = transcriptText || transcriptionRef.current || transcription || '';
        const finalTranscription = completeTranscription.trim();

        // Validate transcript word count
        const wordCount = countWords(finalTranscription);
        if (wordCount < MIN_INTRODUCTION_TRANSCRIPT_WORDS) {
            message.error(`Transcript is too short. Please provide at least ${MIN_INTRODUCTION_TRANSCRIPT_WORDS} words in your introduction.`);
            setIntroductionVerificationFailed(true);
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('recording', blob, 'introduction.webm');
            formData.append('transcript', finalTranscription);

            // uploadFile is a regular async function, not a Redux action, so call it directly
            const resData: any = await uploadFile(`/accel-test/ai-interview/introduction/${id}`, formData);
            if (resData && resData.code === 200) {
                message.success('Introduction recorded successfully');
                setIntroductionRecorded(true);
                setIntroductionBlob(null);
                setIntroductionVerificationFailed(false);
                setInterviewStep('interview');
                loadNextQuestion();
            } else {
                const errorMsg = resData?.message || 'Failed to save introduction recording';
                message.error(errorMsg);
                setIntroductionVerificationFailed(true);
            }
        } catch (error: any) {
            console.error('Error saving introduction recording:', error);
            message.error(error?.message || 'Failed to save introduction recording');
            setIntroductionVerificationFailed(true);
        } finally {
            setIsSubmitting(false);
        }
    }, [id, loadNextQuestion, transcription, transcriptionRef, isSystemCheck]);

    // Stop recording and automatically submit for introduction
    const stopAndSubmitIntroduction = useCallback(() => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Recording stop blocked: System check in progress');
            message.warning('Recording is disabled during system check');
            return;
        }

        if (!mediaRecorderRef.current) {
            console.warn('No media recorder found');
            message.error('No recording found. Please start recording first.');
            return;
        }

        // Check if recording time is too short (less than 10 seconds for introduction)
        if (recordingTime < MIN_INTRODUCTION_RECORDING_TIME) {
            message.warning(`Recording is too short. Please record for at least ${MIN_INTRODUCTION_RECORDING_TIME} seconds.`);
            return;
        }

        // Stop timers first
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (noSpeechTimerRef.current) {
            clearTimeout(noSpeechTimerRef.current);
            noSpeechTimerRef.current = null;
        }
        if (recognitionCheckIntervalRef.current) {
            clearInterval(recognitionCheckIntervalRef.current);
            recognitionCheckIntervalRef.current = null;
        }

        setIsRecording(false);
        isRecordingRef.current = false;

        try {
            const recorder = mediaRecorderRef.current;
            mediaRecorderRef.current = null;

            // Capture recording time before async operations
            const currentRecordingTime = recordingTime;

            recorder.stopRecording(() => {
                try {
                    // Wait a bit for the blob to be fully processed
                    setTimeout(() => {
                        const blob = recorder.getBlob();
                        console.log('Introduction recording blob:', blob ? `size: ${blob.size}, type: ${blob.type}` : 'null');

                        if (blob && blob.size > 0) {
                            console.log('Stopping and submitting introduction, blob size:', blob.size);

                            // Get complete transcription before submitting
                            const completeTranscription = transcriptionRef.current || transcription || '';
                            const finalTranscription = completeTranscription.trim();

                            // Validate recording time
                            if (currentRecordingTime < MIN_INTRODUCTION_RECORDING_TIME) {
                                message.error(`Recording is too short. Please record for at least ${MIN_INTRODUCTION_RECORDING_TIME} seconds.`);
                                setIntroductionBlob(blob);
                                setIntroductionVerificationFailed(true);
                                return;
                            }

                            // Validate transcript word count
                            const wordCount = countWords(finalTranscription);
                            if (wordCount < MIN_INTRODUCTION_TRANSCRIPT_WORDS) {
                                message.error(`Transcript is too short. Please provide at least ${MIN_INTRODUCTION_TRANSCRIPT_WORDS} words in your introduction (currently ${wordCount} words).`);
                                setIntroductionBlob(blob);
                                setIntroductionVerificationFailed(true);
                                return;
                            }

                            // All validations passed, submit
                            setIntroductionBlob(blob);
                            saveIntroductionRecording(blob, finalTranscription);
                        } else {
                            console.error('No blob received from recorder or blob is empty', {
                                blob: blob,
                                size: blob?.size,
                                type: blob?.type
                            });
                            message.error('Recording failed. The recording may be too short or there was an error. Please try again.');
                            setIntroductionVerificationFailed(true);
                        }
                    }, BLOB_PROCESSING_DELAY);
                } catch (error) {
                    console.error('Error in stopAndSubmitIntroduction callback:', error);
                    message.error('Error processing recording. Please try again.');
                    setIntroductionVerificationFailed(true);
                }
            });
        } catch (error) {
            console.error('Error stopping recording:', error);
            message.error('Error stopping recording. Please try again.');
        }
    }, [recordingTime, saveIntroductionRecording, transcription, transcriptionRef, isSystemCheck]);

    const stopRecording = useCallback(() => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Recording stop blocked: System check in progress');
            return;
        }

        // Stop speech recognition first (for interview and introduction steps)
        if ((interviewStep === 'interview' || interviewStep === 'introduction') && recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {
                console.log('Error stopping recognition:', e);
            }
            recognitionRef.current = null;
            setIsTranscribing(false);
        }

        if (mediaRecorderRef.current) {
            try {
                mediaRecorderRef.current.stopRecording(() => {
                    try {
                        const blob = mediaRecorderRef.current?.getBlob();
                        if (blob && blob.size > 0) {
                            console.log('Recording stopped, blob size:', blob.size, 'type:', blob.type);
                            console.log('Current interview step:', interviewStep);

                            // Handle different steps
                            if (interviewStep === 'consent') {
                                console.log('Setting consent blob, size:', blob.size);
                                setConsentBlob(blob);
                                // Force a re-render by updating a state
                                setIsRecording(false);
                            } else if (interviewStep === 'introduction') {
                                console.log('Setting introduction blob, size:', blob.size);
                                setIntroductionBlob(blob);
                                setIsRecording(false);
                            } else {
                                setRecordedBlob(blob);
                            }
                            // Create preview URL and set it to audio element
                            const url = URL.createObjectURL(blob);
                            if (audioRef.current) {
                                // Clean up old URL if exists
                                if (audioRef.current.src) {
                                    URL.revokeObjectURL(audioRef.current.src);
                                }
                                audioRef.current.src = url;
                                audioRef.current.load(); // Force reload to ensure it plays
                            }

                            // Transcription is already done in real-time during recording using Web Speech API
                        } else {
                            console.error('No blob received from recorder or blob is empty', blob);
                            setIsRecording(false);
                        }
                    } catch (error) {
                        console.error('Error in stopRecording callback:', error);
                        setIsRecording(false);
                    }
                });
            } catch (error) {
                console.error('Error stopping recording:', error);
                setIsRecording(false);
            }
            mediaRecorderRef.current = null;
        } else {
            console.warn('No media recorder found when trying to stop recording');
            setIsRecording(false);
        }

        // Don't stop video stream - only stop audio recording
        // Video stream should continue throughout the interview

        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // Clear no-speech timer
        if (noSpeechTimerRef.current) {
            clearTimeout(noSpeechTimerRef.current);
            noSpeechTimerRef.current = null;
        }

        // Clear recognition check interval
        if (recognitionCheckIntervalRef.current) {
            clearInterval(recognitionCheckIntervalRef.current);
            recognitionCheckIntervalRef.current = null;
        }

        setIsRecording(false);
        isRecordingRef.current = false;
    }, [interviewStep, isSystemCheck]);

    // Update audio element when recordedBlob changes
    useEffect(() => {
        if (recordedBlob && audioRef.current) {
            // Clean up old URL if exists
            if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
                URL.revokeObjectURL(audioRef.current.src);
            }
            const url = URL.createObjectURL(recordedBlob);
            audioRef.current.src = url;
            audioRef.current.load();

            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [recordedBlob]);

    // Memoized callbacks for better performance
    const handleResetAnswerState = useCallback(() => {
        resetAnswerState(
            setAnswerSubmitted,
            setRecordedBlob,
            setRecordingTime,
            setTranscription,
            setAnswerScore,
            setPromptSpoken,
            setIsAnswerTimerActive,
            setAnswerTimer,
            transcriptionRef,
            finalTranscriptsRef,
            interimTranscriptRef
        );
    }, []);

    // Memoize current question check - must be before any early returns
    const shouldShowCurrentQuestion = useMemo(() => {
        return currentQuestion && !conversationHistory.some(msg =>
            msg.type === 'question' &&
            msg.content === currentQuestion.question
        );
    }, [currentQuestion, conversationHistory]);

    // Note: Transcription is done in real-time using browser's Web Speech API (frontend only)
    // No AI evaluation - answers are saved directly

    // Auto-submit answer when timer ends
    const autoSaveAnswer = useCallback(async () => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Answer submission blocked: System check in progress');
            return;
        }

        if (!currentQuestion) {
            return;
        }

        // Prevent multiple auto-submissions
        if (isSubmitting || answerSubmitted) {
            console.log('Already submitting or submitted, skipping auto-submit');
            return;
        }

        setIsSubmitting(true);
        try {
            // Get complete transcription - ensure we have all final transcripts from ref
            const completeTranscription = transcriptionRef.current || transcription || '';
            const finalTranscription = completeTranscription.trim();

            // Submit answer with transcription
            const resData = await dispatch(putData(`/accel-test/ai-interview/answer/${id}`, {
                data: encryptData({
                    question: currentQuestion.question,
                    questionNumber: currentQuestion.questionNumber,
                    transcription: finalTranscription,
                    questionId: currentQuestion.questionId || null
                })
            }));

            console.log('Auto-submit answer response:', resData);

            // putData returns response.data.data when code is 200, or response.data when error
            // So if resData has a code property, it's an error
            if (resData && !resData.code) {
                // Success - resData is the data directly
                const data = resData;

                // Add answer to conversation history
                setConversationHistory(prev => [...prev, {
                    type: 'answer',
                    content: finalTranscription || '[Time ended - No answer provided]',
                    questionNumber: currentQuestion.questionNumber,
                    timestamp: new Date()
                }]);

                // Add to answers list
                setAnswers(prev => [...prev, {
                    question: currentQuestion.question,
                    recordingUrl: null,
                    questionNumber: currentQuestion.questionNumber
                }]);

                setAnswerSubmitted(true);

                // Scroll to answer input when answer is added
                scrollToAnswerInput();

                // Auto-load next question after 1.5 seconds
                setTimeout(() => {
                    setIsSubmitting(false);
                    setAnswerSubmitted(false);
                    setRecordedBlob(null);
                    setRecordingTime(0);
                    setTranscription('');
                    setAnswerScore(null);
                    setPromptSpoken(false);
                    setIsAnswerTimerActive(false);
                    setAnswerTimer(ANSWER_TIMER_DURATION);
                    transcriptionRef.current = '';
                    finalTranscriptsRef.current = [];
                    interimTranscriptRef.current = '';
                    loadNextQuestion();
                }, 1500);
            } else {
                const errorMsg = resData?.message || 'Failed to auto-submit answer';
                console.error('Failed to auto-submit answer:', errorMsg, resData);
                message.error(errorMsg);

                setTimeout(() => {
                    setIsSubmitting(false);
                    handleResetAnswerState();
                    loadNextQuestion();
                }, 1500);
            }
        } catch (error) {
            console.error('Error auto-submitting answer:', error);
            message.error('Error auto-submitting answer');

            setTimeout(() => {
                setIsSubmitting(false);
                handleResetAnswerState();
                loadNextQuestion();
            }, 1500);
        }
    }, [currentQuestion, id, dispatch, transcription, transcriptionRef, handleResetAnswerState, loadNextQuestion, setConversationHistory, setAnswers, scrollToAnswerInput, isSystemCheck]);

    const submitAnswer = useCallback(async () => {
        if (!currentQuestion) {
            return;
        }

        // Check if recording is active or if we have transcription (user spoke)
        const hasRecording = recordedBlob !== null;
        const hasTranscription = transcription && transcription.trim().length > 0;
        const isCurrentlyRecording = isRecording;

        // If recording is active, stop it first and wait for blob
        let finalBlob = recordedBlob;
        if (isCurrentlyRecording && mediaRecorderRef.current) {
            console.log('Stopping active recording before submission...');
            finalBlob = await new Promise<Blob | null>((resolve) => {
                const recorder = mediaRecorderRef.current;
                if (!recorder) {
                    resolve(recordedBlob);
                    return;
                }

                // Stop recording and get blob directly from recorder
                recorder.stopRecording(() => {
                    try {
                        const blob = recorder.getBlob();
                        if (blob && blob.size > 0) {
                            console.log('Got blob from recorder, size:', blob.size);
                            setRecordedBlob(blob); // Update state for UI
                            resolve(blob);
                        } else {
                            console.warn('No blob from recorder, using existing recordedBlob');
                            resolve(recordedBlob);
                        }
                    } catch (error) {
                        console.error('Error getting blob from recorder:', error);
                        resolve(recordedBlob);
                    }
                });

                // Fallback: if callback doesn't fire within 3 seconds, use existing blob
                setTimeout(() => {
                    if (!finalBlob) {
                        console.warn('Timeout waiting for blob, using existing recordedBlob');
                        resolve(recordedBlob);
                    }
                }, 3000);
            });

            // Also call stopRecording to clean up state
            stopRecording();

            // Wait a moment for state to update
            await new Promise(resolve => setTimeout(resolve, 500));
        } else {
            // Not recording, use existing blob
            finalBlob = recordedBlob;
        }
        // Get complete transcription - use ref to ensure we have all final transcripts
        const completeTranscription = transcriptionRef.current || transcription || '';
        const finalTranscription = completeTranscription.trim();

        // If no recording blob AND no transcription, treat as skipped
        if (!finalBlob && !finalTranscription) {
            // Add skipped answer to conversation history
            setConversationHistory(prev => [...prev, {
                type: 'answer',
                content: '[Skipped]',
                questionNumber: currentQuestion.questionNumber,
                timestamp: new Date()
            }]);

            // Submit as skipped
            try {
                const resData = await dispatch(putData(`/accel-test/ai-interview/skip/${id}`, {
                    data: encryptData({
                        questionNumber: currentQuestion.questionNumber,
                        questionId: currentQuestion.questionId || null
                    })
                }));

                console.log('Skip question response:', resData);

                if (resData && resData.code === 200) {
                    setAnswerSubmitted(true);

                    // Scroll to answer input when answer is added
                    scrollToAnswerInput();

                    // Auto-load next question after 1 second
                    setTimeout(() => {
                        setAnswerSubmitted(false);
                        setRecordedBlob(null);
                        setRecordingTime(0);
                        setTranscription('');
                        transcriptionRef.current = '';
                        finalTranscriptsRef.current = [];
                        interimTranscriptRef.current = '';
                        handleResetAnswerState();
                        loadNextQuestion();
                    }, 1000);
                } else {
                    const errorMsg = resData?.message || resData?.data?.message || 'Failed to skip question';
                    console.error('Failed to skip question:', errorMsg, resData);
                    message.error(errorMsg);
                    setTimeout(() => {
                        setAnswerSubmitted(false);
                        handleResetAnswerState();
                        loadNextQuestion();
                    }, 1000);
                }
            } catch (error) {
                console.error('Error skipping question:', error);
                message.error('Error skipping question');
                setTimeout(() => {
                    setAnswerSubmitted(false);
                    handleResetAnswerState();
                    loadNextQuestion();
                }, 1000);
            }
            return;
        }

        // Stop timer if active
        if (answerTimerRef.current) {
            clearInterval(answerTimerRef.current);
            answerTimerRef.current = null;
            setIsAnswerTimerActive(false);
        }

        setIsSubmitting(true);
        let resData = null;
        try {
            // Get complete transcription - ensure we have all final transcripts
            const completeTranscription = transcriptionRef.current || transcription || '';
            const answerText = completeTranscription.trim();

            // Submit answer with audio file if available
            if (finalBlob && finalBlob.size > 0) {
                console.log('Submitting answer with audio file:', {
                    blobSize: finalBlob.size,
                    blobType: finalBlob.type,
                    questionNumber: currentQuestion.questionNumber,
                    transcriptionLength: answerText.length
                });

                // Upload audio file with transcription
                const formData = new FormData();
                formData.append('recording', finalBlob, `answer_${currentQuestion.questionNumber}.webm`);
                formData.append('data', encryptData({
                    question: currentQuestion.question,
                    questionNumber: currentQuestion.questionNumber,
                    transcription: answerText,
                    questionId: currentQuestion.questionId || null
                }));

                resData = await uploadFile(`/accel-test/ai-interview/answer/${id}`, formData);
                console.log('Audio upload response:', resData);
            } else {
                // Submit only transcription text (no recording file)
                resData = await dispatch(putData(`/accel-test/ai-interview/answer/${id}`, {
                    data: encryptData({
                        question: currentQuestion.question,
                        questionNumber: currentQuestion.questionNumber,
                        transcription: answerText,
                        questionId: currentQuestion.questionId || null
                    })
                }));
            }

            console.log('Submit answer response:', resData);

            // Handle response from uploadFile (returns { code, data }) or putData
            let data = null;
            if (resData && resData.code === 200) {
                // uploadFile returns { code: 200, data: encryptedData }
                data = decryptData(resData.data);
            } else if (resData && !resData.code) {
                // putData returns data directly when success
                data = resData;
            }

            if (data) {

                // Add answer to conversation history
                setConversationHistory(prev => [...prev, {
                    type: 'answer',
                    content: answerText || '[No answer provided]',
                    questionNumber: currentQuestion.questionNumber,
                    timestamp: new Date()
                }]);

                // Add to answers list with recording URL if available
                setAnswers(prev => [...prev, {
                    question: currentQuestion.question,
                    recordingUrl: data?.recordingUrl || null,
                    questionNumber: currentQuestion.questionNumber
                }]);

                setAnswerSubmitted(true);

                // Scroll to answer input when answer is added
                scrollToAnswerInput();

                // Auto-load next question after 1 second
                setTimeout(() => {
                    setIsSubmitting(false);
                    setAnswerSubmitted(false);
                    setRecordedBlob(null);
                    setRecordingTime(0);
                    setTranscription('');
                    transcriptionRef.current = '';
                    finalTranscriptsRef.current = [];
                    interimTranscriptRef.current = '';
                    handleResetAnswerState();
                    // Clear the last loaded question ID to allow loading the next question
                    lastLoadedQuestionIdRef.current = null;
                    loadNextQuestion();
                }, 1000);
            } else {
                setTimeout(() => {
                    setIsSubmitting(false);
                    // Clear the last loaded question ID to allow loading the next question
                    lastLoadedQuestionIdRef.current = null;
                    loadNextQuestion();
                }, 1000);
            }
        } catch (error) {
            setTimeout(() => {
                setIsSubmitting(false);
                setAnswerSubmitted(false);
                handleResetAnswerState();
                loadNextQuestion();
            }, 1000);
        } finally {
            // isSubmitting will be set to false in the setTimeout callbacks above
        }
    }, [currentQuestion, recordedBlob, transcription, transcriptionRef, id, dispatch, stopRecording, handleResetAnswerState, loadNextQuestion, setConversationHistory, setAnswers, scrollToAnswerInput, isSystemCheck]);

    // Handle case when no question is available - check for next stage
    // This must be before any conditional returns to follow Rules of Hooks
    useEffect(() => {
        if (!currentQuestion && interviewStep === 'interview' && !isLoading && !isLoadingQuestionRef.current && id) {
            console.log('No current question, checking for next stage...');
            loadNextQuestion();
        }
    }, [currentQuestion, interviewStep, isLoading, id]); // Removed loadNextQuestion from dependencies

    // Store loadNextQuestion in a ref to avoid dependency issues
    const loadNextQuestionRef = useRef(loadNextQuestion);
    useEffect(() => {
        loadNextQuestionRef.current = loadNextQuestion;
    }, [loadNextQuestion]);

    // Auto-advance from photo step if photo is already captured (only if we're still on photo step)
    // This is a fallback - the main logic in checkTestLinkInfo should handle step determination
    // This must be before any conditional returns to follow Rules of Hooks
    useEffect(() => {
        if (!isCheckingState && interviewStep === 'photo' && photoCaptured && capturedPhotoUrl) {
            // Photo already exists, automatically move to next step
            if (consentRecorded && introductionRecorded) {
                setInterviewStep('interview');
                interviewStepRef.current = 'interview';
            } else if (consentRecorded) {
                setInterviewStep('introduction');
                interviewStepRef.current = 'introduction';
            } else {
                setInterviewStep('consent');
                interviewStepRef.current = 'consent';
            }
        }
    }, [photoCaptured, capturedPhotoUrl, interviewStep, consentRecorded, introductionRecorded, isCheckingState]);

    // Handle manual section submission (Submit Test button)
    const handleSubmitSection = useCallback(async () => {
        // Show confirmation modal
        Modal.confirm({
            title: 'Submit AI Interview Section',
            content: 'Are you sure you want to submit this section? All remaining questions will be marked as skipped and you will move to the next section.',
            okText: 'Yes, Submit',
            cancelText: 'Cancel',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setIsSubmitting(true);
                    message.loading('Submitting section...', 0);

                    // Stop recording if active
                    if (isRecording && mediaRecorderRef.current) {
                        try {
                            setIsRecording(false);
                            isRecordingRef.current = false;

                            // Stop timers
                            if (timerRef.current) {
                                clearInterval(timerRef.current);
                                timerRef.current = null;
                            }
                            if (noSpeechTimerRef.current) {
                                clearTimeout(noSpeechTimerRef.current);
                                noSpeechTimerRef.current = null;
                            }

                            // Stop speech recognition
                            if (recognitionRef.current) {
                                try {
                                    recognitionRef.current.stop();
                                } catch (e) {
                                    console.log('Error stopping recognition:', e);
                                }
                                setIsTranscribing(false);
                            }

                            // Stop media recorder
                            try {
                                const recorder = mediaRecorderRef.current;
                                await new Promise<void>((resolve) => {
                                    recorder.stopRecording(() => {
                                        try {
                                            const blob = recorder.getBlob();
                                            if (blob && blob.size > 0) {
                                                setRecordedBlob(blob);
                                            }
                                        } catch (error) {
                                            console.error('Error getting blob:', error);
                                        }
                                        resolve();
                                    });
                                });
                                mediaRecorderRef.current = null;
                            } catch (error) {
                                console.error('Error stopping recording:', error);
                            }

                            // Wait for recording to stop
                            await new Promise(resolve => setTimeout(resolve, 500));
                        } catch (error) {
                            console.error('Error stopping recording on submit:', error);
                        }
                    }

                    // Submit current answer if there's one and we're in interview step
                    if (interviewStep === 'interview' && currentQuestion) {
                        const completeTranscription = transcriptionRef.current || transcription || '';
                        const finalTranscription = completeTranscription.trim();
                        const hasRecording = recordedBlob !== null && recordedBlob.size > 0;
                        const hasTranscription = finalTranscription.length > 0;

                        if (hasRecording || hasTranscription) {
                            console.log('Submitting current answer before section submission...');
                            try {
                                await submitAnswer();
                                await new Promise(resolve => setTimeout(resolve, 1000));
                            } catch (error) {
                                console.error('Error submitting answer on section submit:', error);
                                // Try to skip the question if submission fails
                                try {
                                    await dispatch(putData(`/accel-test/ai-interview/skip/${id}`, {
                                        data: encryptData({
                                            questionNumber: currentQuestion.questionNumber,
                                            questionId: currentQuestion.questionId || null
                                        })
                                    }));
                                } catch (skipError) {
                                    console.error('Error skipping question:', skipError);
                                }
                            }
                        } else {
                            // Skip if no answer
                            console.log('No answer found, skipping question before section submission...');
                            try {
                                await dispatch(putData(`/accel-test/ai-interview/skip/${id}`, {
                                    data: encryptData({
                                        questionNumber: currentQuestion.questionNumber,
                                        questionId: currentQuestion.questionId || null
                                    })
                                }));
                                await new Promise(resolve => setTimeout(resolve, 500));
                            } catch (error) {
                                console.error('Error skipping question:', error);
                            }
                        }
                    }

                    // Skip all remaining questions and mark AI interview as complete
                    console.log('Skipping all remaining questions and marking AI interview as complete...');
                    const completeRes = await dispatch(putData(`/accel-test/ai-interview/complete/${id}`, {
                        data: encryptData({})
                    }));

                    message.destroy();

                    if (completeRes && completeRes.code === 200) {
                        const completeData = decryptData(completeRes.data);
                        console.log('AI interview completed:', completeData);
                        if (completeData.skippedCount > 0) {
                            message.success(`AI Interview section submitted. ${completeData.skippedCount} remaining question(s) skipped.`);
                        } else {
                            message.success('AI Interview section submitted successfully.');
                        }

                        // Wait for backend to process
                        await new Promise(resolve => setTimeout(resolve, 1500));

                        // Reload test details to get the latest state
                        const updatedTestRes = await dispatch(getData(`/accel-test/${id}`));
                        if (updatedTestRes && updatedTestRes.code === 200) {
                            const updatedTestData = decryptData(updatedTestRes.data);
                            const hasCodingQuestions = updatedTestData?.coddingQuestions && updatedTestData.coddingQuestions.length > 0;
                            const totalCodingQuestions = updatedTestData?.totalCodingQuestions || 0;
                            const testStage = updatedTestData?.testStage;

                            if (testStage === 'coding' || hasCodingQuestions || totalCodingQuestions > 0) {
                                message.success('Moving to coding section...');
                                if (onSubmit) {
                                    onSubmit();
                                } else {
                                    navigate(`/online-exam/${id}/exam`);
                                }
                            } else if (testStage === 'completed' || (!hasCodingQuestions && totalCodingQuestions === 0)) {
                                message.success('All sections completed! Test submitted successfully.');
                                if (onSubmit) {
                                    onSubmit();
                                } else {
                                    navigate(`/online-test/completed/${id}`);
                                }
                            } else {
                                if (onSubmit) {
                                    onSubmit();
                                } else {
                                    window.location.reload();
                                }
                            }
                        } else {
                            // Fallback
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                navigate(`/online-exam/${id}/exam`);
                            }
                        }
                    } else {
                        message.error('Failed to submit section. Please try again.');
                        setIsSubmitting(false);
                    }
                } catch (error) {
                    console.error('Error submitting section:', error);
                    message.destroy();
                    message.error('An error occurred while submitting the section. Please try again.');
                    setIsSubmitting(false);
                }
            }
        });
    }, [interviewStep, currentQuestion, isRecording, recordedBlob, transcription, submitAnswer, dispatch, id, onSubmit, navigate]);

    // Calculate total time for timer display (must be before conditional returns)
    const calculateTotalTime = () => {
        if (!testDetails?.aiInterviewConfig || testDetails.aiInterviewConfig.length === 0) {
            return 0;
        }
        return testDetails.aiInterviewConfig.reduce((acc: number, item: any) => acc + (item.timeRequired || 0), 0);
    };

    const totalTime = calculateTotalTime();
    const isPureAIInterview = testDetails?.isAIInterview ||
        (testDetails?.totalMCQQuestions === 0 && testDetails?.totalCodingQuestions === 0);
    const shouldShowTimer = examConfig.enableTimer &&
        testDetails?.aiInterviewStartedAt &&
        interviewStep === 'interview' &&
        totalTime > 0 &&
        (testDetails?.testStage === 'ai-interview' || isPureAIInterview) &&
        testDetails?.aiInterviewConfig && testDetails.aiInterviewConfig.length > 0;

    if (totalTime <= 0) {
        console.warn('AI Interview: Total time is 0 or invalid. Timer will not be shown. Config:', testDetails?.aiInterviewConfig);
    }

    // Handle time up - auto-submit and move to next section
    // Must be defined after shouldShowTimer and totalTime are declared
    const handleTimeUp = useCallback(async () => {
        if (timeUpHandledRef.current) {
            console.log('handleTimeUp: Already handled, skipping...');
            return;
        }

        if (interviewStep !== 'interview') {
            console.log('handleTimeUp: Not in interview step, skipping...', interviewStep);
            return;
        }

        if (!shouldShowTimer || totalTime <= 0) {
            console.warn('handleTimeUp: Timer should not be active. shouldShowTimer:', shouldShowTimer, 'totalTime:', totalTime);
            return;
        }

        if (testDetails?.aiInterviewStartedAt) {
            const startDate = new Date(testDetails.aiInterviewStartedAt);
            const endTime = new Date(startDate.getTime() + totalTime * 60 * 1000);
            const now = new Date();
            const timeRemaining = endTime.getTime() - now.getTime();

            if (timeRemaining > 5000) {
                console.warn('handleTimeUp: Time is not actually up. Remaining:', timeRemaining, 'ms. Ignoring timeUp call.');
                return;
            }
        }

        timeUpHandledRef.current = true;
        setIsTimeUp(true);

        console.log('Time is up! Auto-submitting AI interview section...');

        let finalBlobForSubmission: Blob | null = null;
        if (isRecording && mediaRecorderRef.current) {
            try {
                console.log('Stopping recording on time up...');
                setIsRecording(false);
                isRecordingRef.current = false;

                if (timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
                if (noSpeechTimerRef.current) {
                    clearTimeout(noSpeechTimerRef.current);
                    noSpeechTimerRef.current = null;
                }
                if (recognitionCheckIntervalRef.current) {
                    clearInterval(recognitionCheckIntervalRef.current);
                    recognitionCheckIntervalRef.current = null;
                }

                if (recognitionRef.current) {
                    try {
                        recognitionRef.current.stop();
                    } catch (e) {
                        console.log('Error stopping recognition:', e);
                    }
                    setIsTranscribing(false);
                }

                try {
                    const recorder = mediaRecorderRef.current;
                    finalBlobForSubmission = await new Promise<Blob | null>((resolve) => {
                        recorder.stopRecording(() => {
                            try {
                                const blob = recorder.getBlob();
                                if (blob && blob.size > 0) {
                                    console.log('Got blob from recorder on time up, size:', blob.size);
                                    setRecordedBlob(blob);
                                    resolve(blob);
                                } else {
                                    console.warn('No blob from recorder on time up');
                                    resolve(recordedBlob);
                                }
                            } catch (error) {
                                console.error('Error getting blob from recorder:', error);
                                resolve(recordedBlob);
                            }
                        });
                    });
                    mediaRecorderRef.current = null;
                } catch (error) {
                    console.error('Error stopping recording:', error);
                    finalBlobForSubmission = recordedBlob;
                }

                await new Promise(resolve => setTimeout(resolve, 500));
            } catch (error) {
                console.error('Error stopping recording on time up:', error);
                finalBlobForSubmission = recordedBlob;
            }
        } else {
            finalBlobForSubmission = recordedBlob;
        }

        if (interviewStep === 'interview') {
            if (currentQuestion) {
                const completeTranscription = transcriptionRef.current || transcription || '';
                const finalTranscription = completeTranscription.trim();
                const hasRecording = finalBlobForSubmission !== null && finalBlobForSubmission.size > 0;
                const hasTranscription = finalTranscription.length > 0;

                console.log('Time up - checking answer:', {
                    hasRecording,
                    hasTranscription,
                    transcriptionLength: finalTranscription.length,
                    blobSize: finalBlobForSubmission?.size || 0,
                    answerSubmitted
                });

                if (hasRecording || hasTranscription) {
                    console.log('Submitting current answer on time up...');
                    try {
                        if (finalBlobForSubmission && finalBlobForSubmission !== recordedBlob) {
                            setRecordedBlob(finalBlobForSubmission);
                            await new Promise(resolve => setTimeout(resolve, 200));
                        }

                        await submitAnswer();
                        console.log('Answer submitted successfully on time up');
                        await new Promise(resolve => setTimeout(resolve, 1500));
                    } catch (error) {
                        console.error('Error submitting answer on time up:', error);
                        try {
                            await dispatch(putData(`/accel-test/ai-interview/skip/${id}`, {
                                data: encryptData({
                                    questionNumber: currentQuestion.questionNumber,
                                    questionId: currentQuestion.questionId || null
                                })
                            }));
                            console.log('Question skipped after submission error');
                        } catch (skipError) {
                            console.error('Error skipping question after submission error:', skipError);
                        }
                    }
                } else {
                    console.log('No answer found, skipping question on time up...');
                    try {
                        await dispatch(putData(`/accel-test/ai-interview/skip/${id}`, {
                            data: encryptData({
                                questionNumber: currentQuestion.questionNumber,
                                questionId: currentQuestion.questionId || null
                            })
                        }));
                        console.log('Question skipped successfully on time up');
                        await new Promise(resolve => setTimeout(resolve, 500));
                    } catch (error) {
                        console.error('Error skipping question on time up:', error);
                    }
                }
            }

            try {
                console.log('Time up - skipping all remaining questions and marking AI interview as complete...');
                const completeRes = await dispatch(putData(`/accel-test/ai-interview/complete/${id}`, {
                    data: encryptData({})
                }));

                if (completeRes && completeRes.code === 200) {
                    const completeData = decryptData(completeRes.data);
                    console.log('Time up - AI interview completed:', completeData);
                    if (completeData.skippedCount > 0) {
                        message.success(`AI Interview completed. ${completeData.skippedCount} remaining question(s) skipped.`);
                    } else {
                        message.success('AI Interview completed.');
                    }
                } else {
                    console.warn('Time up - complete endpoint returned unexpected response:', completeRes);
                }
            } catch (error) {
                console.error('Error completing AI interview on time up:', error);
            }
        }

        console.log('Time up - preparing to move to next section...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        message.warning("Time's up! AI Interview section completed. Moving to next section...");

        setTimeout(async () => {
            try {
                console.log('Time up - checking for next section...');
                try {
                    const updatedTestRes = await dispatch(getData(`/accel-test/${id}`));
                    if (updatedTestRes && updatedTestRes.code === 200) {
                        const updatedTestData = decryptData(updatedTestRes.data);
                        console.log('Time up - updated test details:', updatedTestData);

                        const hasCodingQuestions = updatedTestData?.coddingQuestions && updatedTestData.coddingQuestions.length > 0;
                        const totalCodingQuestions = updatedTestData?.totalCodingQuestions || 0;
                        const testStage = updatedTestData?.testStage;

                        console.log('Time up - section check:', {
                            hasCodingQuestions,
                            totalCodingQuestions,
                            testStage,
                            hasOnSubmit: !!onSubmit
                        });

                        if (testStage === 'coding' || hasCodingQuestions || totalCodingQuestions > 0) {
                            console.log('Time up - moving to coding section');
                            message.success('Moving to coding section...');
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                navigate(`/online-exam/${id}/exam`);
                            }
                        } else if (testStage === 'completed' || (!hasCodingQuestions && totalCodingQuestions === 0)) {
                            console.log('Time up - submitting test (no more sections)');
                            message.success('All sections completed! Test submitted successfully.');
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                navigate(`/online-test/completed/${id}`);
                            }
                        } else {
                            console.log('Time up - reloading test details...');
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                window.location.reload();
                            }
                        }
                    } else {
                        const hasCodingQuestions = testDetails?.coddingQuestions && testDetails.coddingQuestions.length > 0;
                        const totalCodingQuestions = testDetails?.totalCodingQuestions || 0;

                        if (hasCodingQuestions || totalCodingQuestions > 0) {
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                navigate(`/online-exam/${id}/exam`);
                            }
                        } else {
                            if (onSubmit) {
                                onSubmit();
                            } else {
                                navigate(`/online-test/completed/${id}`);
                            }
                        }
                    }
                } catch (reloadError) {
                    console.error('Error reloading test details on time up:', reloadError);
                    const hasCodingQuestions = testDetails?.coddingQuestions && testDetails.coddingQuestions.length > 0;
                    const totalCodingQuestions = testDetails?.totalCodingQuestions || 0;

                    if (hasCodingQuestions || totalCodingQuestions > 0) {
                        if (onSubmit) {
                            onSubmit();
                        } else {
                            navigate(`/online-exam/${id}/exam`);
                        }
                    } else {
                        if (onSubmit) {
                            onSubmit();
                        } else {
                            navigate(`/online-test/completed/${id}`);
                        }
                    }
                }
            } catch (error) {
                console.error('Error moving to next section on time up:', error);
                message.error('Error moving to next section. Please refresh the page.');
                setTimeout(() => {
                    if (onSubmit) {
                        onSubmit();
                    } else {
                        window.location.reload();
                    }
                }, 1000);
            }
        }, 2500);
    }, [interviewStep, currentQuestion, answerSubmitted, isRecording, recordedBlob, transcription, stopRecording, submitAnswer, id, dispatch, testDetails, onSubmit, navigate, isTimeUp, shouldShowTimer, totalTime]);

    // Calculate if time is actually up by checking the timer (must be before conditional returns)
    const isTimeActuallyUp = useMemo(() => {
        if (!shouldShowTimer || !testDetails?.aiInterviewStartedAt) {
            return false;
        }
        const startDate = new Date(testDetails.aiInterviewStartedAt);
        const endTime = new Date(startDate.getTime() + totalTime * 60 * 1000);
        const now = new Date();
        return now.getTime() >= endTime.getTime();
    }, [shouldShowTimer, testDetails?.aiInterviewStartedAt, totalTime]);

    // Show loading while checking backend state
    if (isCheckingState) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spin size="large" tip="Loading..." />
            </div>
        );
    }

    // Photo Capture Step UI    
    if (interviewStep === 'photo') {
        // If photo is already captured, it should have been skipped by checkTestLinkInfo
        // But if we're here, show the photo capture screen (user can retake if needed)
        return (
            <PhotoCapture
                isSubmitting={isSubmitting}
                onCapturePhoto={(photoBlob) => {
                    setPhotoBlob(photoBlob);
                    setCapturedPhotoUrl(URL.createObjectURL(photoBlob));
                }}
                onRetake={() => {
                    setPhotoBlob(null);
                    setCapturedPhotoUrl(null);
                }}
                capturedPhoto={capturedPhotoUrl}
                onSavePhoto={saveCandidatePhoto}
                testDetails={testDetails}
            />
        );
    }

    // Consent Step UI
    if (interviewStep === 'consent') {
        // If consent is already recorded, it should have been skipped by checkTestLinkInfo
        // But if we're here, show the consent screen (user can re-record if needed)
        return (
            <ConsentRecording
                candidateInfo={candidateInfo}
                isRecording={isRecording}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                recordingTime={recordingTime}
                consentBlob={consentBlob}
                transcription={transcription}
                isTranscribing={isTranscribing}
                videoRef={videoRef}
                streamRef={streamRef}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                onStopAndSubmit={stopAndSubmitConsent}
                onSaveRecording={saveConsentRecording}
                onReRecord={() => {
                    setConsentBlob(null);
                    setRecordingTime(0);
                    setTranscription('');
                    transcriptionRef.current = '';
                    finalTranscriptsRef.current = [];
                    interimTranscriptRef.current = '';
                    setConsentVerificationFailed(false);
                    consentAutoSubmitRef.current = false;
                }}
                consentVerificationFailed={consentVerificationFailed}
            />
        );
    }

    // Introduction Step UI
    if (interviewStep === 'introduction') {
        // If introduction is already recorded, it should have been skipped by checkTestLinkInfo
        // But if we're here, show the introduction screen (user can re-record if needed)
        return (
            <IntroductionRecording
                isRecording={isRecording}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                recordingTime={recordingTime}
                introductionBlob={introductionBlob}
                transcription={transcription}
                isTranscribing={isTranscribing}
                isSpeaking={isSpeaking}
                videoRef={videoRef}
                streamRef={streamRef}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                onStopAndSubmit={stopAndSubmitIntroduction}
                onSaveRecording={saveIntroductionRecording}
                onReRecord={() => {
                    setIntroductionBlob(null);
                    setRecordingTime(0);
                    setTranscription('');
                    transcriptionRef.current = '';
                    finalTranscriptsRef.current = [];
                    interimTranscriptRef.current = '';
                    setIntroductionVerificationFailed(false);
                }}
                introductionVerificationFailed={introductionVerificationFailed}
            />
        );
    }

    if (isLoading && !currentQuestion) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spin size="large" tip="Loading question..." />
            </div>
        );
    }

    if (!currentQuestion && interviewStep === 'interview' && !isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Card>
                    <Spin size="large" tip="Checking for next section..." />
                </Card>
            </div>
        );
    }

    return (
        <div className="bg-[#F3F7F7] flex h-screen flex-col overflow-hidden p-4">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-200 p-4 bg-white rounded-t-lg">
                <img src={require('./../../../assets/logo/logo.png')} alt="logo" className="w-40" />
                <div className="flex items-center gap-4">
                    {shouldShowTimer && (
                        <CountdownTimer
                            durationMinutes={totalTime}
                            startTime={testDetails.aiInterviewStartedAt}
                            timeUp={handleTimeUp}
                        />
                    )}
                    {interviewStep === 'interview' && (
                        <Button
                            type="primary"
                            danger
                            size="large"
                            onClick={handleSubmitSection}
                            disabled={isSubmitting || isTimeUp}
                            loading={isSubmitting}
                        >
                            Submit Test
                        </Button>
                    )}
                </div>
            </div>

            <div className="flex-1 flex gap-4 p-4">
                {/* First Column - AI Avatar with Speaking/Listening States */}
                <div className="w-1/3 flex flex-col gap-4">
                    {/* AI Avatar Area - Sufficient height */}
                    <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '350px', height: 'fit-content', padding: '40px' }}>
                        {/* AI Icon */}
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                                <VideoCameraOutlined style={{ fontSize: '64px', color: '#fff' }} />
                            </div>

                            {/* Status Text */}
                            <div className="text-white text-center">
                                {isSpeaking ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-lg font-semibold">Speaking...</span>
                                        {/* Speaking Animation */}
                                        <div className="flex items-center gap-1">
                                            {[...Array(3)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="w-2 h-8 bg-blue-500 rounded-full"
                                                    style={{
                                                        animation: `speak 0.6s ease-in-out infinite`,
                                                        animationDelay: `${i * 0.2}s`
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : isRecording ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-lg font-semibold">Listening...</span>
                                        {/* Listening Animation */}
                                        <div className="flex items-center gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="w-1 bg-green-500 rounded-full"
                                                    style={{
                                                        animation: `listen 1s ease-in-out infinite`,
                                                        animationDelay: `${i * 0.1}s`,
                                                        height: '20px'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-lg font-semibold">AI Interviewer</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Second Column - Chat History and Answer Input Area */}
                <div className="w-2/3 flex flex-col">
                    <Card
                        className="flex-1 flex flex-col shadow-lg"
                        style={{ borderRadius: '12px' }}
                        bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '24px' }}
                    >
                        <div className="flex items-center justify-between mb-6 pb-4 border-b">
                            <div className="flex items-center gap-3">
                                <Avatar
                                    icon={<RobotOutlined />}
                                    style={{ backgroundColor: '#1890ff' }}
                                    size="large"
                                />
                                <div>
                                    <Title level={4} className="m-0" style={{ marginBottom: '4px' }}>
                                        Interview Conversation
                                    </Title>
                                </div>
                            </div>
                        </div>

                        {/* Chat Conversation History and Answer Input - Single Scrollable View */}
                        <div
                            ref={chatContainerRef}
                            className="flex-1 overflow-y-auto pr-2"
                            style={{
                                maxHeight: 'calc(100vh - 300px)',
                                scrollBehavior: 'smooth'
                            }}
                        >
                            <div className="space-y-4 pb-4">
                                {conversationHistory.map((message) => (
                                    <div
                                        key={`${message.type}-${message.questionNumber || message.timestamp.getTime()}`}
                                        className={`flex items-start gap-3 ${message.type === 'question' ? 'justify-start' : 'justify-end'
                                            }`}
                                    >
                                        {message.type === 'question' ? (
                                            <>
                                                <Avatar
                                                    icon={<RobotOutlined />}
                                                    style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
                                                />
                                                <div className="flex-1 max-w-[75%]">
                                                    <div
                                                        className="bg-blue-50 border border-blue-200 rounded-2xl rounded-tl-sm p-4 shadow-sm"
                                                        style={{
                                                            borderLeft: '4px solid #1890ff'
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Tag color="blue">Question</Tag>
                                                        </div>
                                                        <p className="text-gray-900 font-bold leading-relaxed mb-0" style={{ fontSize: '15px' }}>
                                                            {message.content}
                                                        </p>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex-1 max-w-[75%] flex justify-end">
                                                    <div
                                                        className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl rounded-tr-sm p-4 shadow-sm"
                                                        style={{
                                                            borderRight: '4px solid #52c41a'
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2 mb-2 justify-end">
                                                            <Tag color="green">Your Answer</Tag>
                                                        </div>
                                                        <p className="text-gray-800 leading-relaxed mb-0" style={{ fontSize: '15px' }}>
                                                            {message.content}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Avatar
                                                    icon={<UserOutlined />}
                                                    style={{ backgroundColor: '#52c41a', flexShrink: 0 }}
                                                />
                                            </>
                                        )}
                                    </div>
                                ))}
                                {/* Current Question if not in history */}
                                {shouldShowCurrentQuestion && (
                                    <div className="flex items-start gap-3 justify-start">
                                        <Avatar
                                            icon={<RobotOutlined />}
                                            style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
                                        />
                                        <div className="flex-1 max-w-[75%]">
                                            <div
                                                className="bg-blue-50 border border-blue-200 rounded-2xl rounded-tl-sm p-4 shadow-sm animate-pulse"
                                                style={{
                                                    borderLeft: '4px solid #1890ff'
                                                }}
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Tag color="blue">Current Question</Tag>
                                                </div>
                                                <p className="text-gray-900 font-bold leading-relaxed mb-0" style={{ fontSize: '15px' }}>
                                                    {currentQuestion.question}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Answer Input Section - Inside Scrollable View */}
                                <div ref={answerInputRef} className="pt-6 mt-6 border-t border-gray-200 pb-4">
                                    <div className="flex flex-col">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <Title level={5} className="m-0 flex items-center gap-2">
                                                    <UserOutlined style={{ color: '#52c41a' }} />
                                                    Your Answer
                                                </Title>
                                                {isRecording && (
                                                    <Tag color="red" icon={<AudioOutlined />}>
                                                        Recording...
                                                    </Tag>
                                                )}
                                                {isTranscribing && (
                                                    <Tag color="orange" icon={<Spin size="small" />}>
                                                        Transcribing...
                                                    </Tag>
                                                )}
                                            </div>
                                            {answerTimer >= 0 && isAnswerTimerActive && (
                                                <div className={`flex items-center gap-2 text-sm font-semibold ${answerTimer < 60
                                                    ? 'text-red-600 animate-pulse'
                                                    : 'text-gray-600'
                                                    }`}>
                                                    <ClockCircleOutlined />
                                                    <span>
                                                        {Math.floor(answerTimer / 60)}:{(answerTimer % 60).toString().padStart(2, '0')}
                                                    </span>
                                                    {answerTimer <= 10 && (
                                                        <Tag color="red" style={{ marginLeft: '4px' }}>
                                                            Time Ending!
                                                        </Tag>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            ref={transcriptDisplayRef}
                                            className={`bg-white p-5 rounded-xl border-2 transition-all ${isRecording
                                                ? 'border-red-300 bg-red-50'
                                                : transcription
                                                    ? 'border-green-300 bg-green-50'
                                                    : 'border-gray-200 bg-gray-50'
                                                }`}
                                            style={{
                                                fontSize: '16px',
                                                lineHeight: '1.8',
                                                color: transcription ? '#1f2937' : '#9ca3af',
                                                fontStyle: transcription ? 'normal' : 'italic',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                minHeight: '200px',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                boxShadow: isRecording ? '0 0 20px rgba(239, 68, 68, 0.1)' : 'none'
                                            }}
                                        >
                                            {transcription || (
                                                <div className="flex flex-col items-center justify-center h-full text-center">
                                                    {isRecording ? (
                                                        <>
                                                            <div className="mb-3">
                                                                <AudioOutlined style={{ fontSize: '32px', color: '#ef4444' }} />
                                                            </div>
                                                            <p className="text-red-600 font-medium">Listening... Speak now</p>
                                                            <p className="text-sm text-gray-500 mt-1">Your speech will appear here</p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <SoundOutlined style={{ fontSize: '32px', color: '#9ca3af' }} />
                                                            <p className="mt-3">Start recording to see your transcribed answer here</p>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-4 flex justify-end items-center">
                                            {(isTimeUp && isTimeActuallyUp) ? (
                                                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                                                    <p className="text-red-800 font-semibold mb-0">
                                                        Time's up! The interview section is being submitted automatically...
                                                    </p>
                                                </div>
                                            ) : !answerSubmitted ? (
                                                <Button
                                                    type="primary"
                                                    onClick={submitAnswer}
                                                    size="large"
                                                    loading={isSubmitting}
                                                    style={{
                                                        borderRadius: '8px',
                                                        height: '44px',
                                                        paddingLeft: '24px',
                                                        paddingRight: '24px',
                                                        fontSize: '16px',
                                                        fontWeight: 500
                                                    }}
                                                    disabled={isSubmitting || isSpeaking || !transcription || isTimeUp}
                                                >
                                                    {isSubmitting ? 'Submitting...' : 'Submit Answer'}
                                                </Button>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default AIInterview;